import React, { useState, useRef, useEffect } from 'react';
import { Tag, Input, Progress, Badge } from 'antd';
import { SearchOutlined, BookOutlined, MenuOutlined, CloseOutlined, CheckCircleFilled, LockOutlined } from '@ant-design/icons';
import util from '../../utils/util';

// ─── Role definitions ─────────────────────────────────────────────────────────
const ROLES = {
    user: { label: 'My modules', icon: '👤', color: '#1890ff', desc: 'Guides for the modules assigned to you' },
    admin: { label: 'Full manual', icon: '⚙️', color: '#722ed1', desc: 'Every module guide' },
};

// ─── UI Mockup SVG Components ─────────────────────────────────────────────────
const LoginMockup = () => (
    <svg viewBox="0 0 480 280" style={{ width: '100%', maxWidth: 480, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="480" height="280" fill="#f4f6fb" rx="12"/>
        {/* Top bar */}
        <rect width="480" height="48" fill="#0f172a" rx="0"/>
        <rect y="0" width="480" height="48" fill="#0f172a" rx="12"/>
        <rect y="36" width="480" height="12" fill="#0f172a"/>
        <circle cx="28" cy="24" r="12" fill="#1890ff" opacity="0.7"/>
        <text x="48" y="29" fontFamily="Arial" fontSize="13" fill="#fff" fontWeight="700">QoC Platform</text>
        <text x="340" y="29" fontFamily="Arial" fontSize="11" fill="rgba(255,255,255,0.5)">Eswatini MoH · NQMP</text>
        {/* Login card */}
        <rect x="140" y="68" width="200" height="192" fill="#fff" rx="10" filter="url(#shadow)"/>
        <defs><filter id="shadow"><feDropShadow dx="0" dy="2" stdDeviation="6" floodOpacity="0.08"/></filter></defs>
        <text x="240" y="96" fontFamily="Arial" fontSize="14" fill="#0f172a" fontWeight="800" textAnchor="middle">Sign In</text>
        <text x="240" y="112" fontFamily="Arial" fontSize="10" fill="#94a3b8" textAnchor="middle">Enter your credentials to continue</text>
        {/* Username */}
        <rect x="160" y="122" width="160" height="28" fill="#f8faff" rx="6" stroke="#e2e8f0" strokeWidth="1"/>
        <text x="170" y="140" fontFamily="Arial" fontSize="10" fill="#94a3b8">Username</text>
        {/* Password */}
        <rect x="160" y="158" width="160" height="28" fill="#f8faff" rx="6" stroke="#e2e8f0" strokeWidth="1"/>
        <text x="170" y="176" fontFamily="Arial" fontSize="10" fill="#94a3b8">Password</text>
        {/* Button */}
        <rect x="160" y="196" width="160" height="30" fill="#1890ff" rx="6"/>
        <text x="240" y="215" fontFamily="Arial" fontSize="12" fill="#fff" fontWeight="700" textAnchor="middle">Login</text>
        {/* Clauses at bottom */}
        <text x="240" y="252" fontFamily="Arial" fontSize="9" fill="#94a3b8" textAnchor="middle">AACC · COP · MOM · PRPE · IPCM · PSQI · GROM · FMS · HRE · ISM</text>
    </svg>
);

const DashboardMockup = () => (
    <svg viewBox="0 0 520 300" style={{ width: '100%', maxWidth: 520, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="520" height="300" fill="#f4f6fb" rx="12"/>
        {/* Sidebar */}
        <rect width="110" height="300" fill="#fff" rx="0"/>
        <rect width="110" height="300" fill="#fff" rx="12"/>
        <rect x="98" width="12" height="300" fill="#fff"/>
        <rect x="0" y="0" width="110" height="44" fill="#0f172a" rx="12"/>
        <rect x="0" y="32" width="110" height="12" fill="#0f172a"/>
        <circle cx="22" cy="22" r="8" fill="#1890ff" opacity="0.7"/>
        <text x="34" y="26" fontFamily="Arial" fontSize="9" fill="#fff" fontWeight="700">QoC Platform</text>
        {/* Sidebar items */}
        {[['📋 Assessments', 55], ['✅ Approval', 73], ['📊 Analytics', 91], ['📄 Reports', 109], ['⚙️ Settings', 127]].map(([label, y], i) => (
            <g key={i}>
                <rect x="6" y={y - 10} width="98" height="20" fill={i === 2 ? '#e6f4ff' : 'transparent'} rx="4"/>
                <text x="14" y={y + 4} fontFamily="Arial" fontSize="9" fill={i === 2 ? '#1890ff' : '#64748b'} fontWeight={i === 2 ? '700' : '400'}>{label}</text>
            </g>
        ))}
        {/* Main content */}
        <text x="130" y="30" fontFamily="Arial" fontSize="13" fill="#0f172a" fontWeight="800">Executive Dashboard</text>
        {/* KPI cards */}
        {[['Avg Score', '87.3%', '#1890ff'], ['Assessed', '142', '#52c41a'], ['High Perf', '38', '#389e0d'], ['Attention', '12', '#f5222d']].map(([label, val, color], i) => (
            <g key={i}>
                <rect x={130 + i * 96} y="44" width="88" height="52" fill="#fff" rx="8" stroke={color} strokeWidth="1" strokeOpacity="0.3"/>
                <rect x={130 + i * 96} y="44" width="88" height="4" fill={color} rx="2"/>
                <text x={174 + i * 96} y="72" fontFamily="Arial" fontSize="14" fill={color} fontWeight="900" textAnchor="middle">{val}</text>
                <text x={174 + i * 96} y="86" fontFamily="Arial" fontSize="8" fill="#94a3b8" textAnchor="middle">{label}</text>
            </g>
        ))}
        {/* Chart area */}
        <rect x="130" y="108" width="250" height="120" fill="#fff" rx="8"/>
        <text x="144" y="124" fontFamily="Arial" fontSize="9" fill="#0f172a" fontWeight="700">National Compliance Trend</text>
        {/* Trend line */}
        <polyline points="144,210 180,200 216,195 252,185 288,188 324,178 360,172" fill="none" stroke="#1890ff" strokeWidth="2"/>
        <polyline points="144,210 180,200 216,195 252,185 288,188 324,178 360,172 360,220 144,220" fill="#1890ff" fillOpacity="0.08"/>
        <line x1="144" y1="188" x2="360" y2="188" stroke="#52c41a" strokeDasharray="4,3" strokeWidth="1"/>
        <text x="362" y="191" fontFamily="Arial" fontSize="7" fill="#52c41a">90%</text>
        {/* Right panel */}
        <rect x="390" y="108" width="120" height="120" fill="#fff" rx="8"/>
        <text x="404" y="124" fontFamily="Arial" fontSize="9" fill="#0f172a" fontWeight="700">Score Distribution</text>
        <circle cx="450" cy="172" r="34" fill="none" stroke="#e8e8e8" strokeWidth="16"/>
        <circle cx="450" cy="172" r="34" fill="none" stroke="#52c41a" strokeWidth="16" strokeDasharray="85 130" strokeDashoffset="-32"/>
        <circle cx="450" cy="172" r="34" fill="none" stroke="#1890ff" strokeWidth="16" strokeDasharray="45 130" strokeDashoffset="-117"/>
        <text x="450" y="176" fontFamily="Arial" fontSize="9" fill="#0f172a" fontWeight="700" textAnchor="middle">142</text>
        {/* Facility list */}
        <rect x="130" y="238" width="380" height="52" fill="#fff" rx="8"/>
        <text x="144" y="254" fontFamily="Arial" fontSize="9" fill="#0f172a" fontWeight="700">Top Facilities</text>
        {[['🥇 Mbabane Gov. Hospital', '97.2%', '#d4af37'], ['🥈 Mankayane Hospital', '94.8%', '#a8a8a8'], ['🥉 Pigg\'s Peak Hospital', '92.1%', '#b08d57']].map(([name, score, color], i) => (
            <g key={i}>
                <text x="144" y={266 + i * 11} fontFamily="Arial" fontSize="8" fill="#334155">{name}</text>
                <text x="492" y={266 + i * 11} fontFamily="Arial" fontSize="8" fill={color} fontWeight="700" textAnchor="end">{score}</text>
            </g>
        ))}
    </svg>
);

const AssessmentMockup = () => (
    <svg viewBox="0 0 520 300" style={{ width: '100%', maxWidth: 520, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="520" height="300" fill="#f4f6fb" rx="12"/>
        <rect width="520" height="44" fill="#0f172a" rx="12"/>
        <rect y="32" width="520" height="12" fill="#0f172a"/>
        <text x="20" y="28" fontFamily="Arial" fontSize="13" fill="#fff" fontWeight="800">Assessment — AACC · Mbabane Government Hospital · FY2025/26</text>
        <rect x="420" y="10" width="80" height="24" fill="#52c41a" rx="6"/>
        <text x="460" y="26" fontFamily="Arial" fontSize="10" fill="#fff" fontWeight="700" textAnchor="middle">Submit ✓</text>
        {/* Score bar */}
        <rect x="20" y="56" width="480" height="32" fill="#fff" rx="8"/>
        <text x="32" y="76" fontFamily="Arial" fontSize="11" fill="#0f172a" fontWeight="700">Clause Score:</text>
        <text x="130" y="76" fontFamily="Arial" fontSize="14" fill="#52c41a" fontWeight="900">87.3%</text>
        <rect x="220" y="64" width="200" height="16" fill="#f0f0f0" rx="8"/>
        <rect x="220" y="64" width="175" height="16" fill="#52c41a" rx="8"/>
        <text x="432" y="76" fontFamily="Arial" fontSize="10" fill="#94a3b8">★★★ Good</text>
        {/* ME rows */}
        {[
            ['AACC.1.1', 'Admission procedures are documented and communicated', 'Met', '#52c41a'],
            ['AACC.1.2', 'Patient assessment completed within defined timeframe', 'Met', '#52c41a'],
            ['AACC.1.3 ⚠ KCS', 'Emergency protocols are posted and accessible', 'Partially Met', '#fa8c16'],
            ['AACC.1.4', 'Referral pathways are documented', 'Not Met', '#f5222d'],
            ['AACC.1.5', 'Discharge planning initiated on admission', 'Met', '#52c41a'],
        ].map(([code, text, status, color], i) => (
            <g key={i}>
                <rect x="20" y={96 + i * 38} width="480" height="34" fill="#fff" rx="6" stroke="#f0f0f0" strokeWidth="1"/>
                <text x="32" y={110 + i * 38} fontFamily="Arial" fontSize="9" fill="#1890ff" fontWeight="700">{code}</text>
                <text x="32" y={123 + i * 38} fontFamily="Arial" fontSize="9" fill="#334155">{text}</text>
                <rect x="390" y={100 + i * 38} width="80" height="20" fill={color} fillOpacity="0.12" rx="5" stroke={color} strokeWidth="1" strokeOpacity="0.4"/>
                <text x="430" y={114 + i * 38} fontFamily="Arial" fontSize="8" fill={color} fontWeight="700" textAnchor="middle">{status}</text>
            </g>
        ))}
    </svg>
);

const ApprovalMockup = () => (
    <svg viewBox="0 0 520 260" style={{ width: '100%', maxWidth: 520, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="520" height="260" fill="#f4f6fb" rx="12"/>
        <rect width="520" height="44" fill="#0f172a" rx="12"/>
        <rect y="32" width="520" height="12" fill="#0f172a"/>
        <text x="20" y="28" fontFamily="Arial" fontSize="13" fill="#fff" fontWeight="800">Approval Queue</text>
        <rect x="400" y="10" width="100" height="24" fill="#1890ff" rx="6"/>
        <text x="450" y="26" fontFamily="Arial" fontSize="10" fill="#fff" fontWeight="700" textAnchor="middle">Bulk Approve</text>
        {/* Filter row */}
        <rect x="20" y="54" width="480" height="28" fill="#fff" rx="6"/>
        {['All Facilities', 'All Clauses', 'Submitted', 'This Period'].map((f, i) => (
            <g key={i}>
                <rect x={28 + i * 118} y="58" width="110" height="18" fill="#f8faff" rx="4" stroke="#e2e8f0" strokeWidth="1"/>
                <text x={83 + i * 118} y="70" fontFamily="Arial" fontSize="9" fill="#64748b" textAnchor="middle">{f}</text>
            </g>
        ))}
        {/* Table header */}
        <rect x="20" y="90" width="480" height="22" fill="#f0f5ff" rx="4"/>
        {['', 'Facility', 'Clause', 'Date', 'Score', 'Status', 'Action'].map((h, i) => (
            <text key={i} x={[30, 50, 200, 260, 320, 370, 440][i]} y="105" fontFamily="Arial" fontSize="9" fill="#1a1a2e" fontWeight="700">{h}</text>
        ))}
        {/* Rows */}
        {[
            ['Mbabane Gov. Hospital', 'AACC', '12 Jul 26', '87.3%', 'Submitted'],
            ['Mankayane Hospital', 'COP', '11 Jul 26', '92.1%', 'Submitted'],
            ['Pigg\'s Peak Hospital', 'IPCM', '10 Jul 26', '74.8%', 'Submitted'],
            ['Hlatikhulu Hospital', 'FMS', '09 Jul 26', '95.6%', 'Submitted'],
        ].map(([fac, clause, date, score, status], i) => (
            <g key={i}>
                <rect x="20" y={116 + i * 32} width="480" height="28" fill={i % 2 ? '#fafbff' : '#fff'} rx="4"/>
                <rect x="24" y={122 + i * 32} width="16" height="16" fill="#f0f0f0" rx="3" stroke="#e2e8f0" strokeWidth="1"/>
                <text x="50" y={134 + i * 32} fontFamily="Arial" fontSize="9" fill="#334155">{fac}</text>
                <rect x="196" y={121 + i * 32} width="36" height="16" fill="#e6f4ff" rx="4"/>
                <text x="214" y={133 + i * 32} fontFamily="Arial" fontSize="8" fill="#1890ff" fontWeight="700" textAnchor="middle">{clause}</text>
                <text x="260" y={134 + i * 32} fontFamily="Arial" fontSize="9" fill="#94a3b8">{date}</text>
                <text x="320" y={134 + i * 32} fontFamily="Arial" fontSize="9" fill="#52c41a" fontWeight="700">{score}</text>
                <rect x="362" y={121 + i * 32} width="60" height="16" fill="#fffbe6" rx="4"/>
                <text x="392" y={133 + i * 32} fontFamily="Arial" fontSize="8" fill="#fa8c16" fontWeight="700" textAnchor="middle">{status}</text>
                <rect x="430" y={121 + i * 32} width="52" height="16" fill="#1890ff" rx="4"/>
                <text x="456" y={133 + i * 32} fontFamily="Arial" fontSize="8" fill="#fff" fontWeight="700" textAnchor="middle">Review →</text>
            </g>
        ))}
    </svg>
);

const SettingsMockup = () => (
    <svg viewBox="0 0 520 260" style={{ width: '100%', maxWidth: 520, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="520" height="260" fill="#f4f6fb" rx="12"/>
        <rect width="520" height="44" fill="#0f172a" rx="12"/>
        <rect y="32" width="520" height="12" fill="#0f172a"/>
        <text x="20" y="28" fontFamily="Arial" fontSize="13" fill="#fff" fontWeight="800">Access Control · Role Permissions</text>
        {/* Tabs */}
        <rect x="20" y="54" width="150" height="24" fill="#fff" rx="6" stroke="#1890ff" strokeWidth="1.5"/>
        <text x="95" y="70" fontFamily="Arial" fontSize="10" fill="#1890ff" fontWeight="700" textAnchor="middle">Role Permissions</text>
        <rect x="178" y="54" width="150" height="24" fill="#f8faff" rx="6" stroke="#e2e8f0" strokeWidth="1"/>
        <text x="253" y="70" fontFamily="Arial" fontSize="10" fill="#94a3b8" textAnchor="middle">Module Management</text>
        {/* Role cards */}
        {[['Facility Assessor', '3 modules', '#52c41a'], ['Approval Officer', '2 modules', '#fa8c16'], ['National Analyst', '4 modules', '#722ed1'], ['QoC Administrator', '8 modules', '#1890ff']].map(([role, modules, color], i) => (
            <g key={i}>
                <rect x={20 + (i % 2) * 250} y={88 + Math.floor(i / 2) * 80} width="232" height="68" fill="#fff" rx="8" stroke={color} strokeWidth="1" strokeOpacity="0.3"/>
                <rect x={20 + (i % 2) * 250} y={88 + Math.floor(i / 2) * 80} width="232" height="4" fill={color} rx="2"/>
                <text x={36 + (i % 2) * 250} y={110 + Math.floor(i / 2) * 80} fontFamily="Arial" fontSize="11" fill="#0f172a" fontWeight="700">{role}</text>
                <rect x={36 + (i % 2) * 250} y={118 + Math.floor(i / 2) * 80} width="60" height="16" fill={color} fillOpacity="0.1" rx="4"/>
                <text x={66 + (i % 2) * 250} y={130 + Math.floor(i / 2) * 80} fontFamily="Arial" fontSize="9" fill={color} fontWeight="700" textAnchor="middle">{modules}</text>
                <rect x={180 + (i % 2) * 250} y={118 + Math.floor(i / 2) * 80} width="56" height="20" fill={color} rx="6"/>
                <text x={208 + (i % 2) * 250} y={132 + Math.floor(i / 2) * 80} fontFamily="Arial" fontSize="9" fill="#fff" fontWeight="700" textAnchor="middle">Edit →</text>
            </g>
        ))}
    </svg>
);

const SurveyMockup = () => (
    <svg viewBox="0 0 400 320" style={{ width: '100%', maxWidth: 400, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="400" height="320" fill="#f4f6fb" rx="12"/>
        <rect width="400" height="52" fill="#006837" rx="12"/>
        <rect y="40" width="400" height="12" fill="#006837"/>
        <text x="200" y="22" fontFamily="Arial" fontSize="12" fill="#fff" fontWeight="800" textAnchor="middle">CUSTOMER SATISFACTION SURVEY</text>
        <text x="200" y="38" fontFamily="Arial" fontSize="9" fill="rgba(255,255,255,0.7)" textAnchor="middle">Ministry of Health · NQMP · Please tick your answer</text>
        {/* Progress */}
        <rect x="20" y="62" width="360" height="8" fill="#e8f5e9" rx="4"/>
        <rect x="20" y="62" width="216" height="8" fill="#006837" rx="4"/>
        <text x="200" y="84" fontFamily="Arial" fontSize="9" fill="#006837" fontWeight="700" textAnchor="middle">60% complete</text>
        {/* Questions */}
        {[
            ['Q1', 'How long did you wait before receiving service?', ['< 30 Min ✓', '30-60 Min', '1-2 Hours', '> 2 Hours'], 0],
            ['Q2', 'How was the attitude of the health care provider?', ['Very Good ✓', 'Good', 'Average', 'Poor'], 0],
            ['Q3', 'How satisfied were you with the service received?', ['Very Satisfied', 'Satisfied ✓', 'Unsatisfied', 'Very Unsatisfied'], 1],
        ].map(([ref, text, opts, selected], i) => (
            <g key={i}>
                <rect x="20" y={96 + i * 68} width="360" height="60" fill="#fff" rx="8" stroke="#e2e8f0" strokeWidth="1"/>
                <rect x="20" y={96 + i * 68} width="3" height="60" fill="#006837" rx="2"/>
                <text x="32" y={113 + i * 68} fontFamily="Arial" fontSize="9" fill="#006837" fontWeight="700">{ref}</text>
                <text x="54" y={113 + i * 68} fontFamily="Arial" fontSize="9" fill="#334155">{text}</text>
                {opts.map((opt, j) => (
                    <g key={j}>
                        <rect x={32 + j * 86} y={121 + i * 68} width="80" height="22" fill={j === selected ? '#e8f5e9' : '#f8faff'} rx="5" stroke={j === selected ? '#006837' : '#e2e8f0'} strokeWidth={j === selected ? 1.5 : 1}/>
                        <text x={72 + j * 86} y={136 + i * 68} fontFamily="Arial" fontSize="8" fill={j === selected ? '#006837' : '#64748b'} fontWeight={j === selected ? '700' : '400'} textAnchor="middle">{opt}</text>
                    </g>
                ))}
            </g>
        ))}
    </svg>
);

// ─── Manual content ───────────────────────────────────────────────────────────
const SECTIONS_USER = [
    {
        id: 'getting-started',
        icon: '🚀',
        title: 'Getting Started',
        color: '#1890ff',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'gs-overview',
                title: 'What is the QoC Platform?',
                readTime: 3,
                graphic: <LoginMockup />,
                content: `
The Eswatini Quality of Care (QoC) Platform is the Ministry of Health's digital tool for managing, tracking, and reporting on health facility quality assessments across the country.

**What it does:**
- Manages structured audits against 10 QoC standard clauses
- Tracks compliance scores from audit through to approval
- Provides national-level analytics dashboards
- Collects inpatient and outpatient patient satisfaction surveys
- Generates detailed audit reports (PDF and Excel)
- Supports bulk import from KoboCollect and Excel templates
- Collects adolescent and youth service (AYS) scorecards and runs the Health Excellence Awards
- Supports infection prevention (IPC) assessments, survey tools, M&E tracking and MOH reports
- Shows each user only the modules their role has been given

**The 10 QoC Clauses:**

| # | Code | Title |
|---|---|---|
| 1 | AACC | Access, Assessment and Continuity of Care |
| 2 | COP | Care of the Patient |
| 3 | MOM | Management of Medication |
| 4 | PRPE | Patient Rights and Patient Education |
| 5 | IPCM | Infection Prevention and Control Management |
| 6 | PSQI | Patient Safety and Quality Improvement |
| 7 | GROM | Governance, Roles and Responsibilities of Management |
| 8 | FMS | Facility Management, Safety and Support Services |
| 9 | HRE | Human Resource and Education |
| 10 | ISM | Information System Management |
                `
            },
            {
                id: 'gs-login',
                title: 'Logging In',
                readTime: 2,
                content: `
Navigate to the platform URL provided by your administrator. Enter your **username** and **password** and click Login.

The login page displays the 10 QoC clauses — these are fetched live from the system so any new standards added by your administrator appear here automatically.

**Your permissions control what you see.** The navigation menu shows only the sections your role has been granted access to. If you expect to see a section that isn't showing, contact your administrator to check your role permissions. This manual works the same way: it only shows guides for the modules you have.

**Permission keys:** An administrator gives each role a set of modules in Settings > Access Control. These are the main ones:

| Module key | What it unlocks |
|---|---|
| \`qoc_assess\` | Create and submit assessments, including IPC assessments |
| \`qoc_import\` | Import assessments from KoboCollect or Excel |
| \`qoc_approve\` | Review, approve, reject, or request revision |
| \`qoc_executive_dash\`, \`qoc_facility_report\` | Executive Dashboard and facility reports |
| \`ays_submit\`, \`ays_admin\`, \`ays_analytics\` | AYS scorecard submission, management and analytics |
| \`qoc_standards\`, \`qoc_periods\`, \`qoc_schedules\` | Standards setup, assessment periods, audit schedules |
| \`qoc_access_control\`, \`manage_roles_users\` | Role permissions, users and accounts |

There is no single "admin" permission. Administrative access is the sum of the modules a role has been given.

**Forgot your password?** Contact your system administrator. There is no self-service password reset.
                `
            },
            {
                id: 'gs-navigation',
                title: 'Getting Around',
                readTime: 2,
                content: `
The platform uses a **left sidebar** for navigation. Click any menu item to open that page. **You only see the menus for the modules your role has been given**, so your sidebar may be shorter than the list below.

**Menu groups:**

| Group | What it covers |
|---|---|
| Administration | Users and roles, facilities, this User Manual, and the navigation manager |
| Quality of Care | Standards setup, assessments, approval, KoBo import, corrective actions, audit team, facility profile |
| Adolescent and Youth Services | SRH scorecard, AYS management and AYS analytics |
| Customer Satisfaction | Patient survey data and facility QR codes |
| Health Excellence Awards | Nominations, programme assessments, judge portal, awards dashboard |
| IPC | Infection prevention and control assessments and analytics |
| Analytics and Dashboards | Analytics, compliance, quality index, executive dashboard, reports, activity trail, SMT alerts |
| System Configuration | KPI targets, audit schedules, assessment periods, access control, HIE facility sync |
| M&E Tracker | Frameworks, assignments, progress logging and verification, SDG report |
| MOH Reports | Indicator and data submission, review and sign-off, report generation |
| Survey Builder | Survey tools, templates and the review queue |

**Tip:** If a menu you expect is missing, see "Cannot See a Page" under Troubleshooting. Menu names may differ slightly from the group names above.
                `
            },
        ]
    },
    {
        id: 'assessments',
        icon: '📋',
        title: 'Assessments',
        color: '#52c41a',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'as-create',
                title: 'Creating an Assessment',
                readTime: 4,
                graphic: <AssessmentMockup />,
                content: `
1. Go to **Quality of Care → Assessments**
2. Click **New Assessment**
3. Select the **Facility** and **Audit Period**
4. Select the **Clause** being assessed (e.g. AACC, COP)
5. Set the **Audit Date** and assign the **Lead Assessor**
6. Click **Save** to create a draft

Each assessment covers **one clause**. A full facility audit with all 10 clauses creates 10 separate assessment records, which are grouped together in reports and dashboards by period.

**Assessment statuses:**

| Status | Meaning |
|---|---|
| Draft | Created but not yet submitted |
| Submitted | Submitted for review — visible in dashboards |
| Approved | Approved — final status, score locked |
| Rejected | Returned — requires correction before resubmission |
| Revision Requested | Approver flagged specific items for correction |

**Important:** Only Submitted and Approved assessments appear in dashboards and analytics. Drafts are excluded.
                `
            },
            {
                id: 'as-responses',
                title: 'Recording Measurable Elements',
                readTime: 5,
                content: `
Once an assessment is open, fill in responses for each **Measurable Element (ME)** — the specific questions within the clause.

**Fulfillment options:**

| Option | Score | When to use |
|---|---|---|
| Met | 100% | Requirement is fully satisfied |
| Partially Met | 50% | Requirement is partially satisfied |
| Not Met | 0% | Requirement is not satisfied |
| Not Applicable | Excluded | Does not apply to this facility type |
| Not Checked | Excluded | Could not be assessed during this audit |

**Scoring formula:**
- A = Total audited MEs (Met + Partially Met + Not Met only — NA and NC are excluded)
- Score per Met ME = 100 ÷ A
- Clause Score = (Met × Score) + (Partially Met × Score ÷ 2)

**⚠ Key Critical Standards (KCS):** MEs marked KCS are critical. Failure on a KCS is flagged immediately and highlighted in red in reports.

For each ME you can also record:
- **Comment** — Required when Not Met or Partially Met
- **General Observation** — Descriptive note
- **Criticality** — High / Medium / Low
- **Area of Good Practice** — Positive findings
                `
            },
            {
                id: 'as-submit',
                title: 'Submitting for Approval',
                readTime: 2,
                content: `
When all MEs are completed:

1. Review the **clause score** shown at the top of the assessment
2. Click **Submit for Approval**
3. The status changes to "Submitted" and the assessment appears in the approver's queue

**After submission:**
- The assessment is visible in dashboards and analytics immediately
- You cannot edit a submitted assessment unless the approver rejects it or requests a revision
- If rejected, you will receive the rejection reason and can edit and resubmit

**Rating scale:**

| Score | Rating | Stars |
|---|---|---|
| 98 – 100% | Outstanding | ★★★★★ |
| 95 – 97.9% | Excellent | ★★★★ |
| 90 – 94.9% | Good | ★★★ |
| 85 – 89.9% | Fair | ★★ |
| 70 – 84.9% | Requires Improvement | ★ |
| 0 – 69.9% | Inadequate | — |
                `
            },
            {
                id: 'as-import',
                title: 'Importing from KoboCollect',
                readTime: 4,
                content: `
If assessments were conducted offline using KoboCollect tablets:

1. Go to **Quality of Care → Import (KoBo)**
2. Select the **Clause** the export covers — this is critical
3. Select the **Facility** and **Period**
4. Upload the exported KoboCollect file (.xlsx or .csv)
5. Review the mapped responses in the preview table
6. Click **Import** to save all records

**Why you must select the correct clause first:**
KoboCollect exports ME codes in short format (e.g. \`1.1\`) but the database stores prefixed codes (e.g. \`AACC.1.1\`). The system automatically applies the clause prefix during import based on your selection.

**Excel template import:**
1. Download the offline template from Assessment Import
2. Fill in fulfillment responses using the provided dropdowns
3. Upload the completed file
4. Review the mapped data and confirm

**Common issue — duplicate entry error:**
The same assessment has already been imported. Check the assessments list before re-importing.
                `
            },
        ]
    },
    {
        id: 'approval',
        icon: '✅',
        title: 'Approval Workflow',
        color: '#fa8c16',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'ap-review',
                title: 'Reviewing Assessments',
                readTime: 3,
                graphic: <ApprovalMockup />,
                content: `
Users with the \`qoc_approve\` permission access the **Approval Queue** under Quality of Care.

The queue shows all submitted assessments with facility name, clause badge, audit date, score, star rating, and submission timestamp.

Click **Review** to open the full assessment and see all ME responses.

**Actions available:**

| Action | Effect |
|---|---|
| Approve | Marks as approved; score is finalised in analytics |
| Reject | Returns to assessor with a required rejection reason |
| Request Revision | Flags specific MEs for correction with notes |

Filter the queue by status, facility, clause, or period using the filter bar at the top.
                `
            },
            {
                id: 'ap-bulk',
                title: 'Bulk Approval',
                readTime: 1,
                content: `
Use the checkboxes on the approval list to select multiple assessments, then click **Bulk Approve** to approve all selected records at once.

This is useful when a facility has submitted all 10 clause assessments on the same date and all are ready for approval.

**Best practice:** Always review at least a sample of the assessments before bulk approving. The review page shows all ME responses, comments, and any KCS failures flagged in red.
                `
            },
        ]
    },
    {
        id: 'dashboards',
        icon: '📊',
        title: 'Dashboards & Analytics',
        color: '#722ed1',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'db-executive',
                title: 'Executive Dashboard',
                readTime: 5,
                graphic: <DashboardMockup />,
                content: `
The **Executive Dashboard** (Reports → Executive Dashboard) is the primary national-level analytics view. It has 10 sections navigable from a sticky tab bar at the top.

| Section | What it shows |
|---|---|
| Overview | KPI cards: avg score, facilities assessed, high performers, needs attention |
| Trend | 12-month national compliance trend with 90% target and 85% benchmark |
| Schedules | Audit schedule status: completed, in progress, overdue, upcoming |
| Clauses | Radar chart, clause bar chart, heatmap by facility |
| Sub-Standards | Per-clause average score cards |
| Low MEs | Clause results below 70% flagged for attention |
| KPI vs Actuals | Gauge charts showing performance against defined targets |
| Quality Index | Composite QoC + survey weighted index per facility |
| Survey Dashboard | Inpatient/outpatient satisfaction survey results |
| Period Comparison | Multi-cycle comparison (up to 5 periods) |

**The main filter bar** (sticky at top): Set Facility, Level, Clause, Date Range, and Period — all sections update together.
                `
            },
            {
                id: 'db-filters',
                title: 'Using Filters',
                readTime: 2,
                content: `
The sticky filter bar at the top of the Executive Dashboard has 5 filters:

| Filter | Effect |
|---|---|
| Facility | Show data for one specific facility only |
| Level | Filter by facility type (e.g. Hospital, Clinic) |
| Clause | Filter all charts to one clause |
| Date Range | Manual date window override |
| Assessment Period | Snap to a defined period's exact date range |

Selecting an **Assessment Period** automatically adjusts the date range to match that period's start and end dates.

Click **Clear filters ×** to reset all filters back to the national view.

**Tip:** The Period Comparison section inherits your facility, level, and clause filters but uses its own cycle selectors for the date dimension.
                `
            },
            {
                id: 'db-period',
                title: 'Period Comparison',
                readTime: 3,
                content: `
The **Period Comparison** section (Section 10 of the Executive Dashboard) lets you compare up to 5 audit periods side by side.

**How to use:**
1. Scroll to Period Comparison or click "🔄 Period Comparison" in the tab bar
2. Use the Cycle 1–5 selectors to choose which periods to compare
3. Filters from the main bar automatically apply

**What it shows:**
- Overall score cards per period + a Δ change card (last vs first cycle)
- Clause grouped bar chart — all clauses, one bar per period
- Clause trend lines — trajectory across cycles
- Clause table — scores, stars, and delta per period
- Facility delta chart — which facilities improved or regressed
- Facility table — all facility scores across selected periods

**Note:** Requires at least 2 active periods with assessment data configured in Settings → Assessment Periods.
                `
            },
        ]
    },
    {
        id: 'reports',
        icon: '📄',
        title: 'Reports & Exports',
        color: '#eb2f96',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'rp-facility',
                title: 'Facility Audit Report',
                readTime: 3,
                content: `
The **Facility Report** (Reports → Facility Report) generates a comprehensive audit report for a selected facility.

**To generate:**
1. Select the facility
2. Select the assessment period — look for entries showing "(10 clauses)"
3. The report loads in the preview panel automatically

**Export options:**

| Format | Best for |
|---|---|
| Print / Save PDF | Quick sharing via browser |
| Narrative PDF Report | Formal submissions — full narrative text per clause |
| Download Excel | Data analysis — one sheet per clause |

**Narrative PDF structure:**
- Cover page with facility details, score, and star rating
- Executive summary with narrative paragraph
- Table of contents and abbreviations
- Introduction, scope, objectives, methodology
- Findings per clause: summary table + nonconformity table
- Corrective Action Plan (if CAP items exist)
- Conclusion with improvement recommendations

**Important:** Always select the grouped period entry showing the clause count (e.g. "10 clauses") for the full report rather than a single-clause entry.
                `
            },
            {
                id: 'rp-excel',
                title: 'Excel Export',
                readTime: 2,
                content: `
The Excel export produces a workbook with:
- **Cover Page** sheet: facility info, overall score, and section scores table
- **One sheet per clause** (AACC through ISM) with all ME responses

Each clause sheet has:
- Colour-coded cells (green = Met, orange = Partial, red = Not Met)
- Auto-filter on header row
- Frozen panes to keep the header visible while scrolling
- KCS MEs highlighted in red

The file is named: \`QoC_Assessment_{FacilityName}_{Date}.xlsx\`
                `
            },
            {
                id: 'rp-card-export',
                title: 'Chart & Dashboard Export',
                readTime: 2,
                content: `
Every chart card in the Executive Dashboard has **PNG** and **PDF** export buttons in its top-right corner.

- **PNG** — Captures the card at 2.5× scale as a high-resolution image
- **PDF** — Captures at 2.0× scale on A4 landscape with a dark header stripe

The **Download PDF** button in the dashboard header captures all 10 sections into a single multi-page A4 landscape PDF.

**Tip:** Export buttons are hidden during capture so they don't appear in the output image.
                `
            },
        ]
    },
    {
        id: 'surveys',
        icon: '📝',
        title: 'Customer Surveys',
        color: '#13c2c2',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'sv-public',
                title: 'How Patients Submit Surveys',
                readTime: 3,
                graphic: <SurveyMockup />,
                content: `
Patients submit satisfaction surveys without logging in using a **QR code** generated from the Facility Coordinates page. Scanning the QR code opens the public survey form on the patient's phone.

**Inpatient survey** — For patients admitted to a ward. Covers: staff attitude, supplies, cleanliness, food, communication, and overall satisfaction.

**Outpatient survey** — For patients seen in outpatient departments. Covers: waiting times, staff attitude, cleanliness, medication received, and overall satisfaction.

**How QR codes work:**
- Each facility has a unique QR code generated from its GPS coordinates
- The system checks that the patient is physically at the facility when submitting
- One submission is allowed per device per survey type per facility per year
- Surveys are automatically linked to the current active period

**Geolocation requirement:** Surveys must be submitted within 100 metres of the facility. If a patient is outside this range, they will see a location error.
                `
            },
            {
                id: 'sv-manage',
                title: 'Viewing Survey Data',
                readTime: 2,
                content: `
Go to **National Oversight → Customer Surveys** to:
- View all submitted surveys in a sortable table
- Filter by facility, period, survey type, and date range
- View overall scores and satisfaction ratings
- See the Survey Analytics Dashboard (collapsible panel at the top)

**Scoring:** Each survey item is scored 0–100. The overall score is the mean of all scored items.

**Satisfaction threshold:** A survey scoring ≥ 70% is considered "satisfied." The Executive Dashboard shows the percentage of surveys meeting this threshold.

**Quality Index integration:** Survey scores are weighted with QoC audit scores (default: 20% survey / 80% QoC) to produce the Quality Index shown in National Oversight → Quality Index.

**Survey Analytics Dashboard:**
The collapsible panel at the top of the Customer Surveys page shows:
- Average Score by Facility — bar charts, Inpatient and Outpatient separately, sorted highest to lowest
- Score by Section — progress bars per survey section
- Satisfaction Distribution — breakdown of satisfaction levels
- Medications Not Received — which medications patients reported not receiving
                `
            },
        ]
    },
        {
            id: 'ays-scorecard',
            title: 'AYS Scorecard',
            icon: '📋',
            color: '#1890ff',
            roles: ['user', 'admin'],
            topics: [
                { id: 'ays-overview', title: 'Overview', readTime: 2, content: `The **AYS (Adolescent and Youth Services) Scorecard** captures patient feedback in enrolled facilities. Peer Educators submit monthly scorecards rating provider behaviour on 24 questions.

**Scale:** Always=4, Mostly=3, Sometimes=2, Never=1
**Threshold:** Mean 3.5+ = Satisfied` },
                { id: 'ays-submit', title: 'Submitting a Scorecard', readTime: 3, roles: ['user'], content: `1. Log in - you land on AYS Scorecard automatically
2. Select month and year
3. Click Open Scorecard
4. Rate each question
5. Save draft at any time
6. Click Submit when all questions are answered` },
                { id: 'ays-admin', title: 'Administration', readTime: 3, roles: ['admin'], content: `Go to **AYS Scorecard - Administration**.

**Questions tab:** Add, edit, deactivate questions.

**Enrolled Facilities tab:** Enrol facilities to allow scorecard submission.` },
                { id: 'ays-dashboard', title: 'AYS Dashboard', readTime: 3, roles: ['admin'], content: `The **Dashboard** tab on AYS Analytics shows client satisfaction as six themes: Key Population, Confidentiality and Privacy, Young Mothers/Currently Pregnant, Interpersonal Manner of HCP, Health Facility Services and HCP Competency.

**Filters:** Region, Facility, Year and Month can be combined. Leave a filter on "All" to widen the view. Choosing a region narrows the facility list. Turn on **Include submitted** to count scorecards that are not yet approved; by default only approved scorecards are counted.

**How scores are shown:** Each question's average score is converted to a percentage (score divided by 4, times 100). The question that is worded negatively ("Sometimes I do not get the services I want") is reverse scored, so a higher percentage always means better performance.

**Colours:** Every percentage uses the QoC rating scale: Outstanding 98% and above, Excellent 95%, Good 90%, Fair 85%, Requires Improvement 70%, Inadequate below 70%.

**Unanswered questions** show "No data" and are left out of the averages, not counted as zero.

**Trend:** The line chart at the bottom shows the overall monthly score for the current filters.

**Exporting:** Use **Export PNG** or **Export PDF**. The file header shows the facility (or region) and the period selected.` },
                { id: 'ays-analytics', title: 'AYS Analytics', readTime: 2, roles: ['admin'], content: `Go to **AYS Scorecard - AYS Analytics**.

**Questions tab:** National mean score per question.

**By Facility tab:** Each enrolled facility ranked by mean score and satisfaction status.` },
            ],
        },
        {
            id: 'health-awards',
            title: 'Health Excellence Awards',
            icon: '🏆',
            color: '#d97706',
            roles: ['user', 'admin'],
            topics: [
                { id: 'enhea-overview', title: 'ENHEA Overview', readTime: 3, content: `The **Health Excellence Awards (ENHEA)** recognises outstanding health facilities and programmes annually.

**Facility Awards** - QoC assessments for nominated facilities ranked by facility type.
**Programs Awards** - 14-criteria tool (1-5 scale): Best Public Health Program, Best Administration Department.

**Final Score:** 70% NQMP + 30% Judge Average` },
                { id: 'enhea-nominations', title: 'Facility Nominations', readTime: 3, roles: ['admin'], content: `Go to **Health Excellence Awards - Facility Nominations**.

Nominate facilities for the awards cycle. Can be done before or after assessment.

1. Click Nominate Facilities
2. Select award cycle
3. Select facilities
4. Click Nominate

Award category assigned automatically by facility type. Can withdraw and reinstate at any time.` },
                { id: 'enhea-programs', title: 'Programs Assessment', readTime: 5, content: `Go to **Health Excellence Awards - Programs Assessment**.

Score 14 criteria across 4 sections (1-5):
- Strategic Foundations (SF-1 to SF-3)
- Operational Excellence (OE-1 to OE-4)
- Organizational Capacity (OC-1 to OC-3)
- Service Quality and Innovation (SQI-1 to SQI-4)

**Bands:** Best Practice Champion 85%+, Strong Performer 64%+, Developing Performer 43%+, Needs Support` },
                { id: 'enhea-approval', title: 'Approval and Revision', readTime: 3, roles: ['admin'], content: `Go to **Programs Assessment - Approval Queue**.

**Approve:** Finalises the assessment.
**Return:** Sends back to assessor with a mandatory comment explaining what needs correction.` },
                { id: 'enhea-report', title: 'Narrative Report', readTime: 2, content: `Once approved, click **Report** to download a Word document with:
1. Cover page
2. Overall score and band
3. Introduction narrative
4. Assessment findings table
5. Identified gaps (score 2 or below)
6. Recommendations table
7. Conclusion` },
                { id: 'enhea-judge', title: 'Judge Portal', readTime: 4, roles: ['admin'], content: `Go to **Health Excellence Awards - Judge Portal**.

Score facilities on 8 criteria (1-5 stars, total out of 40):
1. Clinical Excellence
2. Patient Safety
3. Innovation
4. Service Efficiency
5. Community Engagement
6. Leadership
7. Documentation
8. Alignment with Ministry Goals

Estimated final score (70% NQMP + 30% judge) updates live.` },
                { id: 'enhea-dashboard', title: 'Awards Dashboard', readTime: 2, content: `Go to **Health Excellence Awards - Awards Dashboard**.

**Facility Awards:** Nominated facilities ranked by QoC score per award category.

**Programs and Units:** Approved programme assessments ranked by score.

**Individual Awards:** Manual process for 2026.` },
            ],
        },
    {
        id: 'survey-builder',
        icon: '\u{1F528}',
        title: 'Survey Builder',
        color: '#2f54eb',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'svb-overview',
                title: 'Survey Builder Overview',
                readTime: 3,
                content: `
The **Survey Builder** is where surveys and data collection tools are created and managed. It is available to users who have the Manage Survey Tools module.

The page lists your surveys with their status, collection period and number of responses. Summary cards at the top show Total Surveys, Published, Total Responses and Drafts. National users can also filter the list by MoH programme.

**What the buttons on each survey do:**

| Button | What it does |
|---|---|
| Build | Add and edit the questions |
| Stats | Analytics for the responses received |
| Team icon | Invite and manage data collectors |
| People icon | Share the survey with colleagues and export it as an XLSForm |
| Chart icon | Collection progress: coverage, pace and pending facilities |
| Download icon | Export responses to Excel or CSV |
| Link icon | Create or revoke a public link |
| Publish / Close | Open the survey for collection, or close it |
| Edit / Delete | Change the survey details, or remove the survey |

**Survey status:** Draft means the survey is still being built. Published means it is collecting data. Closed means it no longer accepts responses.

**Programme users:** If you work for an MoH programme, the programme is filled in automatically and cannot be changed.
                `
            },
            {
                id: 'svb-build',
                title: 'Creating and Building a Survey',
                readTime: 5,
                content: `
Click **New Survey** at the top of the Survey Builder.

**Survey details:**

| Field | Notes |
|---|---|
| Survey Title | Required. For example: Facility Readiness Assessment 2026 |
| Description | A short note on the purpose of the survey |
| MoH Programme | Optional, unless you are a programme user (then it is automatic) |
| Category | Choose the closest category |
| Target Audience | All Facilities, By Region, By Facility Type, or a Specific Facility |
| Collection Period | Start and end dates for collection |
| Allow Multiple Submissions | Turn on if more than one submission should be accepted |
| Anonymous Responses | Turn on if the survey should collect responses anonymously |

Click **Save and Build Questions** to continue.

**Building the questions:** Click **Build** on the survey.

1. Click **Add Question** and type the question text
2. Choose the question type
3. Tick **Required** if the question must be answered
4. Add optional helper text under the question
5. Group questions into **Sections** for longer tools
6. Click **Save Questions**

**Question types:**

| Type | Use it for |
|---|---|
| Short Text, Long Text | Free-text answers |
| Number | Counts and quantities |
| Single Choice | One answer from a short list |
| Multiple Choice | Several answers from a list |
| Dropdown (Select) | One answer from a long list |
| Matrix / Grid | Several sub-questions (rows) answered on the same options (columns) |
| Rating Scale | A star-style rating |
| Yes / No | A simple yes or no |
| Date | A date |

**Skip logic:** Under a question, use **Show only if** to display it only when an earlier answer matches a condition, such as equals or contains. A question that is hidden is not treated as required.

**Calculated fields** cannot be added with Add Question. They come in through XLSForm import (see the next topic).
                `
            },
            {
                id: 'svb-import',
                title: 'Importing an XLSForm',
                readTime: 4,
                content: `
Use **Import XLSForm** at the top of the Survey Builder to bring in a form designed in Kobo or Excel (.xlsx file).

1. Click **Import XLSForm** and drop in the .xlsx file
2. Review the preview: questions found, questions that will import, sections, and anything skipped
3. Confirm the import

**What happens to the survey:**
- If no survey with the same title exists, a new one is created in the Program Evaluation category
- If one already exists, you are asked whether to replace its questions with the imported ones

**What carries over:** choice lists, required flags, hints, sections (groups), repeat groups and skip logic from the relevant column.

**Calculated fields:** Rows of type calculate are imported as calculated questions. On the public and collector forms they fill in automatically while the form is completed, for example domain scores, a total, a percentage and a status. Respondents cannot type into them. Display-only notes are read-only as well.

**Skipped items:** Any XLSForm type the platform does not support is listed under Skipped with the reason. Check this list before publishing.

**Tip:** You can download an existing survey as an XLSForm from the Collaborators screen, edit it offline and import it again.
                `
            },
            {
                id: 'svb-collect',
                title: 'Publishing and Collecting Data',
                readTime: 5,
                content: `
A draft survey does not collect data. Click **Publish** and confirm to open it. Click **Close** when collection is finished.

There are two ways to collect data.

**1. Public link**
1. Click the link icon and generate a public link
2. Copy the link and share it
3. Respondents open it, select their facility and complete the form

- Anyone with the link can submit. Use **Revoke Link** to switch it off
- Before the collection start date, the form says collection has not started. After the end date, it says the survey has closed
- A new submission for a facility that has already responded replaces the earlier one

**2. Data collectors**
Click the team icon to open **Data Collectors**, then invite a collector by email.

| Field | Notes |
|---|---|
| Email Address | Required |
| Full Name | Optional |
| Limit to Region | Optional. Restricts the collector to one region |
| Specific Facilities | Optional. Restricts the collector to chosen facilities |
| Sections | For long tools, choose which sections the collector may complete |

Each collector receives an email with a unique link. **No login is needed.** From the list you can copy or open a collector's link, and revoke access at any time.

**Collectors assigned to sections:** Once any collector is assigned to specific sections, only the survey owner or a co-owner can do the final submission for the tool. Pick a facility in the progress panel to see which sections are assigned to whom and whether each has been captured. When every section is complete, click **Mark as Submitted**.
                `
            },
            {
                id: 'svb-collab',
                title: 'Sharing a Survey with Colleagues',
                readTime: 3,
                content: `
Click the people icon on a survey to open **Collaborators**. Enter an email address (a registered user or a new one), choose a role and send the invitation. Invited people show as Pending until they accept.

**Roles:**

| Role | What they can do |
|---|---|
| Co-Owner | Full access: edit, manage collectors, view analytics |
| Editor | Edit questions and survey settings |
| Collector Manager | Manage data collectors only |
| Viewer | View analytics and responses only |

You can change a collaborator's role or remove them at any time.

**XLSForm download:** The same screen lets you download the survey as an XLSForm (.xlsx). Use it to edit offline or to reuse the survey elsewhere.
                `
            },
            {
                id: 'svb-progress',
                title: 'Progress, Analytics and Export',
                readTime: 3,
                content: `
**Collection Progress** (chart icon) shows how data collection is going:

| Item | What it shows |
|---|---|
| Submitted | Facilities that have submitted |
| Coverage | Percentage of target facilities covered |
| Days Remaining | Days left in the collection period |
| Pace Needed | The daily submissions needed to finish on time |

Below the cards you will see a daily submission chart, progress per collector, recent submissions, and the list of **Pending Facilities** that have not submitted yet. Filter by collector or by region to focus on one group.

**Stats** shows the analytics for the responses received: total responses, number of facilities and number of questions, with a summary for each question. Number questions also show the mean, minimum and maximum.

**Export** (download icon) saves the responses as an Excel or CSV file for analysis.
                `
            },
            {
                id: 'svb-templates',
                title: 'Template Library and National Review',
                readTime: 3,
                content: `
**Using a template:** The **National Template Library** holds standardised tools approved by national. Search for a tool, check its description, number of questions and how many regions have adopted it, then click **Use This Tool**. A copy is added to your own surveys, where you can customise it and start collecting.

**Sharing your own tool:** On a survey you created, click **Submit for Review**. The survey then shows **Pending National Review**. If it is approved it shows **Published as Template**, and a copy appears in the library.

**For national reviewers:** The **Survey Review Queue** lists tools submitted by regions, with the survey name, who submitted it and when.

| Action | Effect |
|---|---|
| Approve and Publish | Publishes a copy to the National Template Library. The original stays with its creator, unchanged |
| Reject | Returns the tool to its owner as private. You can add a reason so they know what to improve |

The Template Library and Review Queue screens are for national users. Regional users send tools for review from the Survey Builder.
                `
            },
        ],
    },
    {
        id: 'ipc',
        icon: '\u{1F9FC}',
        title: 'Infection Prevention and Control',
        color: '#13c2c2',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'ipc-assess',
                title: 'IPCAF Assessment',
                readTime: 5,
                content: `
The **IPCAF Assessment** (IPC menu group) captures the WHO Infection Prevention and Control Assessment Framework for a facility. It is available to users who can do QoC assessments.

**Starting an assessment:**
1. Select the **facility**
2. Set the **assessment date**
3. Click **Start New Assessment**

Below the form, **Past IPCAF Assessments** lists the facility's earlier assessments with their status (draft or submitted) and IPC level. Click **Open** to continue a draft, or the eye icon to view a submitted one.

**How the assessment is laid out:**
- There is one tab for each of the 8 core components, CC1 to CC8
- Each question has a reference number and a list of answer options, and each option carries points
- **Single choice** questions take one answer. **Multiple choice** questions take every option that applies, and the points of the ticked options are added together
- Each question shows the points it currently scores, and each tab shows its section score out of 100

**Scoring:** The total is out of 800 and decides the IPC level.

| Total score | IPC level |
|---|---|
| 0 - 200 | Inadequate |
| 201 - 400 | Basic |
| 401 - 600 | Intermediate |
| 601 - 800 | Advanced |

**Saving and submitting:** The progress bar shows how many questions are answered. Click **Save Draft** at any time. **Submit** only works when every question is answered. If some are missing, you will see how many are still unanswered.

**Working offline with Excel:**
1. Click **Export Excel** to download the assessment. This locks it for offline work and records who has it
2. Complete the file without a connection
3. Click **Import Excel** to load the responses back in. The import reports any problems it finds

While an assessment is locked, other users see a notice with the name of the person working offline and should not capture responses online. If the file is lost, the lock can be cleared from the banner. Check with the person first.

**Two people in the same assessment:** If someone else has it open, you see a warning that responses may conflict. Coordinate before submitting.
                `
            },
            {
                id: 'ipc-analytics',
                title: 'IPCAF Analytics',
                readTime: 3,
                content: `
**IPCAF Analytics** (IPC menu group) shows how facilities are performing on infection prevention and control.

**National view:**
- **Facilities Assessed** and **National Avg Score** at the top
- **IPC Level Distribution**: how many facilities fall in each level
- **Average Score by Region**
- **Weakest Indicators**: the bottom indicators nationally
- A table of **all 84 indicators** ranked from weakest to strongest, tagged by core component (CC1 to CC8)

**Facility scorecard:** Select a facility to see its latest total score and IPC level. If the facility has no submitted IPCAF assessment yet, you will see a notice instead. Charts mark the level boundaries at 200, 400 and 600.

**Exporting:** Every chart card has **PNG** and **PDF** buttons in its corner.

Use the weakest indicators and the regional averages to decide where to focus support and training.
                `
            },
        ],
    },
    {
        id: 'cap',
        icon: '\u{1F6E0}',
        title: 'Corrective Action Plans',
        color: '#fa8c16',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'cap-manage',
                title: 'Managing Corrective Actions',
                readTime: 4,
                content: `
**Corrective Actions** (Quality of Care menu group) tracks what each facility will do about the findings from its assessments.

**Summary cards:** Open, In Progress, Overdue, Closed, Cancelled, and the **Closure Rate**. The closure rate is green at 70% or higher and orange below that.

**Filters:** Period, Facility and Status. The page opens on the current period. Use the reset button to clear the filters. Items are grouped by facility, and each facility card shows its open and overdue counts.

**What the list shows:**

| Column | Meaning |
|---|---|
| Clause | The QoC clause the finding belongs to |
| Finding | The nonconformity, tagged Not Met (red) or Partially Met (orange) |
| Root Cause | Why the gap exists |
| Action | The corrective action planned |
| Responsible | Who will do it |
| Target Date | Shown as overdue when the date has passed and the item is not closed |
| Status | Open, In Progress, Overdue, Closed or Cancelled |

**Generating items automatically:**
1. Select a **period**
2. Click **Generate from Assessments**
3. Confirm

The system creates an item for every Not Met and Partially Met response in the **approved** assessments of that period. Items that already exist are not duplicated. If there are no approved assessments for the period, you will be told.

**Adding or editing an item:** Use the add button, or the edit icon on a row. **Facility** and **Finding** are required. You can also set the period, fulfillment, root cause, corrective action, responsible person, target date and status.

**Closing an item:** Click the tick icon on a row and confirm to mark it as verified and closed. Items can also be deleted after a confirmation. Changes to status are logged.
                `
            },
            {
                id: 'cap-commitment',
                title: 'The Commitment Planner',
                readTime: 4,
                content: `
The **Commitment Planner** lets you capture a facility's commitments for all its open findings in one place.

**Opening it:** Select a facility in the filter and click the commitment planner button in the header, or use the button on a facility card. It pulls **every open finding for that facility, from any period**, grouped by period.

**For each finding you can record:**
- The **root cause**, from a fixed list
- The corrective action
- The responsible person
- The target date
- The status

Where the assessment checklist shows missing evidence, a warning lists the checklist items that were not verified (for example 3 of 5 verified), so the facility knows what to produce.

Click **Save Commitments** to save everything in one go. The message tells you how many items were saved and how many failed. If the facility has no open findings, you will see a note saying so.

**Root cause options:**

| Root cause |
|---|
| Policy gap |
| Staff knowledge / training |
| Infrastructure / equipment |
| Resource / supply shortage |
| Process / workflow issue |
| Supervision gap |
| Documentation failure |
| Other |

**Working offline with Excel:** Click **Export Excel** to download the findings, complete the file together with the facility, then click **Import Excel** to load it back. The import reports any rows with problems, so you can correct them and import again. The Excel file includes a legend for the root cause and status codes.
                `
            },
        ],
    },
    {
        id: 'facility-info',
        icon: '\u{1F3E2}',
        title: 'Facility Information',
        color: '#2f9e44',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'fp-profile',
                title: 'Facility Profile',
                readTime: 4,
                content: `
The **Facility Profile** holds the standing facts about a facility. Search for a facility to open its profile.

**Facility Details** shows the facility name, level, region, ownership, accreditation, catchment population, bed capacity, overall staffing (approved and actual) and implementing partners.

**Staffing by Cadre:** a table with the approved posts, the actual staff and the percentage filled for each cadre.

**Services Offered:** a checklist grouped into categories. Each service is marked Yes, No or Not applicable, with an optional note.

| Category |
|---|
| Outpatient and General |
| Reproductive, Maternal, Newborn and Child Health |
| Communicable Diseases |
| Non-Communicable Diseases |
| Mental and Neurological Health |
| Special Needs / Protection |
| Diagnostics and Support Services |
| Other |

**Updating the profile:** Click **Edit Profile**, change the details, staffing and services, and save. You will see a confirmation that the profile was updated. For accreditation, choose the facility's status (for example Not Accredited or ISO certified). For implementing partners, list the organisations working in the facility, such as MSF, UNICEF or PEPFAR.

**Assessment history:** The profile also lists the facility's past assessments with the audit date, clauses, score, rating, assessor and status.
                `
            },
            {
                id: 'fp-dashboard',
                title: 'Facility Profiling Dashboard',
                readTime: 3,
                content: `
The **Facility Profiling Dashboard** shows how complete facility profiles are across the country, and what they say in total.

**Filters:** Region and Facility Level.

**Cards:** Facilities in view, Services checklist complete, and Staffing breakdown complete.

**Services:** For each category, a table shows every service with the number of facilities that answered Yes, No or Not applicable.

**Staffing:** A national table by cadre shows the approved posts, the actual staff, the fill rate and how many facilities are reporting.

**Facility lists:** A coverage list shows each facility's region, level, and whether its services and staffing are complete, with a button to open its profile. A staffing list shows the approved, actual and fill rate per facility.

Use it to find facilities with incomplete profiles and the cadres with the biggest gaps.
                `
            },
            {
                id: 'team-registry',
                title: 'Audit Team Registry',
                readTime: 4,
                content: `
The **Audit Team Registry** records who carried out each facility audit.

The list shows the facility, audit date, lead auditor, team members, departments visited and period. Filter by period or facility.

**Recording an audit team:** Click the add button.

| Field | Notes |
|---|---|
| Facility | Required |
| Audit Date | Required |
| Period | The audit period |
| Team members | Pick a user from the system, or type a name if the person is not in the system. Add as many members as needed |
| Role | Lead Auditor, Auditor, Technical Expert, Observer or National Office Representative |
| Departments visited | OPD, Pharmacy, Laboratory, Maternity, Radiology, Wards, ANC, ARV Clinic, TB Clinic, Emergency, Theatre, Stores, Admin, Management |
| Meeting notes | Notes on the opening meeting (attendees and key discussions) and the closing meeting (findings presented and the facility's responses) |
| Observers | Names of any observers, such as WHO or NGO partners |

Save the record. Records can be edited or deleted after a confirmation.
                `
            },
        ],
    },
    {
        id: 'oversight',
        icon: '\u{1F4CA}',
        title: 'National Oversight Dashboards',
        color: '#722ed1',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'ov-compliance',
                title: 'Compliance Dashboard',
                readTime: 4,
                content: `
The **Compliance Dashboard** tracks whether planned audits are happening on time.

**Filters:** Period, and Quarter. Quarters are worked out from the selected period's own start date, not the calendar year. If a period starts on 1 April, Quarter 1 is April to June. You can select more than one quarter. Choose a period first to use the quarter filter. Audits scheduled for the Health Excellence Awards are not shown here.

**Cards:** The page counts the scheduled audits as Completed, In Progress, Scheduled, Overdue and Cancelled, and shows the average score of completed audits that have a score.

**The list:**

| Column | Meaning |
|---|---|
| Facility | The facility being audited |
| Clause | The clause covered |
| Scheduled Date | When the audit was planned |
| Lead Assessor | The assessor in charge |
| Status | Completed, In Progress, Scheduled or Cancelled. An audit shows as Overdue when its date has passed and it is not completed |
| Score | The score, coloured by the QoC rating scale |

Audits are planned under Settings > Audit Schedules.
                `
            },
            {
                id: 'ov-qi',
                title: 'Quality Index',
                readTime: 4,
                content: `
The **Quality Index** combines a facility's QoC audit score with its patient survey score into one number.

**How it is weighted:** By default the QoC score counts for 80% and the survey score for 20%. Within the survey score, inpatient and outpatient surveys count for 50% each. The result is rated on the QoC scale, from Outstanding (98% and above) to Inadequate (below 70%).

**Cards:** Total Facilities, Avg Quality Index, Top Performers (90% and above) and Needs Attention (below 70%).

**Filters:** Facility, Period and Region.

**The table** shows each facility's QoC score, survey score, Quality Index, rating and the date it was calculated.

**Calculating an index:**
1. Click **Calculate Quality Index**
2. Choose the **facility**
3. Enter the **period** name and its start and end dates (all four are required)
4. Click **Calculate**

Calculate again after new assessments are approved or new surveys arrive, so the index stays current.
                `
            },
            {
                id: 'ov-smt',
                title: 'SMT Alerts Dashboard',
                readTime: 3,
                content: `
The **SMT Alerts Dashboard** gives senior management national signals from across the programmes on one page. Each card is green when things are fine, and turns to a warning or a critical alert when a threshold is crossed. The thresholds shown are proposed defaults and can be adjusted. Use **Refresh** to reload.

| Card | What it checks |
|---|---|
| Corrective Action Plans | Actions overdue by more than 30 days. It becomes critical when any are more than 90 days overdue |
| IPCAF Maturity | Facilities assessed at the Inadequate infection prevention level |
| QoC Compliance | Clauses with an average below 60% |
| Patient Satisfaction | Share of survey responses that are Unsatisfied or Very Unsatisfied, flagged above 25% |
| Facility Profiling Coverage | Share of facilities with a complete services checklist, flagged below 70%. The staffing completion is shown underneath |
| Staffing Shortages | Cadres nationally below a 50% fill rate |

Click through to the related page to follow up on any card that is not green.
                `
            },
            {
                id: 'ov-trail',
                title: 'Activity Trail',
                readTime: 3,
                content: `
The **Activity Trail** is a log of what users do on the platform. It is used for audit, support and accountability.

**Cards:** Total Events, Unique Users, Modules Touched and Critical Actions.

**Filters:** User, Event type, Module, From Date, To Date, and a search box for actions.

**What it records:** Logins and logouts, records created, updated and deleted, assessments submitted, approved, rejected or sent back for revision, corrective actions created and updated, changes to standards, HIE syncs, reports generated or exported, file uploads and downloads, page views, and errors.

Click **Export CSV** to download the events that match the current filters.

**Tip:** To investigate a change, filter by the user and the date, then narrow by event type.
                `
            },
        ],
    },
    {
        id: 'me-tracker',
        icon: '\u{1F3AF}',
        title: 'M&E Tracker',
        color: '#0958d9',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'me-overview',
                title: 'M&E Tracker Overview',
                readTime: 3,
                content: `
The **M&E Tracker** monitors progress against a framework such as a standard, a workplan, a strategy or the SDGs. Each part of the work is done by a different role, and each person only sees the screens they have been given.

| Step | Screen | Who does it |
|---|---|---|
| 1. Define the framework and its indicators | Framework Library | M&E administrators |
| 2. Assign the framework to facilities | Assignments | M&E administrators |
| 3. Record progress against each indicator | Log Progress | Facility staff |
| 4. Check the evidence and approve or return it | Verify Progress | M&E officers and assessors |
| 5. Follow overall progress | M&E Dashboard | Managers |
| 6. Report on SDG 3 and NHSSP indicators | SDG Progress Report | Managers and planners |

**How the pieces connect:** A framework has sections, and each section has indicators. An assignment links a framework to a facility for a period. Facilities log progress on each indicator, and a verifier approves it. Only verified work counts as confirmed progress on the dashboard and reports.
                `
            },
            {
                id: 'me-frameworks',
                title: 'Framework Library and Structure',
                readTime: 5,
                content: `
The **Framework Library** lists every framework. Click the add button to create one.

**Framework details:**

| Field | Notes |
|---|---|
| Framework Name | Required. For example ISO 9001:2015 Quality Management |
| Short Code | Required. For example ISO9001 |
| Framework Type | Required: Standard, Workplan, Strategy, SDG / MDG, Policy, Programme or Other |
| Issuing Body | For example ISO, MoH, UN, Cabinet |
| Version / Edition | For example 2015 or FY2025/26 |
| Effective Date, End Date | When the framework applies |
| Scope | National, Regional, Facility or Department |
| Applicable Facility Types | Leave empty for all types |
| Description | What the framework covers and its objectives |
| Status | New frameworks start as Draft. Only active frameworks appear on the M&E Dashboard |
| Allow Self-Reporting | Lets facilities report on themselves |
| Require Evidence | Marks that evidence is expected for each indicator |

**Row actions:** View Details, **Build Structure** (sections and indicators), Edit and Delete. A framework cannot be deleted while it has assignments.

**Building the structure:** Click **Build Structure**.

**Sections** group indicators. Give each a code (such as 6, 6.1 or SA2), a title, an optional parent section for sub-sections, a description, a weight and a display order.

**Indicators** are what gets measured:

| Field | Notes |
|---|---|
| Code, Title / Requirement | The title is required |
| Detailed Description | The full requirement or activity |
| Type | Requirement, Output, Outcome, Process, Milestone or KPI |
| Measurement Unit | Yes / No, Percentage, Count, Score, Date or Narrative only |
| Baseline, Target | For example Baseline: current state. Target: 100% or 5000 |
| Reporting Frequency | Once only, Monthly, Quarterly, Bi-annual or Annual |
| Data Source | Where the evidence comes from |
| Responsible Role | The job title responsible |
| Verification Method | How the assessor will verify it |
| Weight, Order | How much it counts and where it sits in the list |
                `
            },
            {
                id: 'me-assign',
                title: 'Framework Assignments',
                readTime: 4,
                content: `
**Assignments** link a framework to a facility for a reporting period. Use the search box to find an assignment by facility or framework.

**Creating an assignment:**
1. Click the add button
2. Choose the **framework**
3. Choose the **facility**. Switch to bulk mode to select several facilities at once and create all the assignments in one go
4. Set the **implementation period** and the **target completion date**
5. Choose the **M&E supervisor or assessor**
6. Enter the **reporting period** (for example FY2025/26 or Q1 2026) and a **period label** (for example Annual or Q1)
7. Add notes if needed, then save

**Assignment status:**

| Status | Meaning |
|---|---|
| Not Started | Work has not begun |
| In Progress | Work is under way |
| On Track | Progressing as planned |
| Delayed | Behind schedule |
| Completed | All work done |
| Certified | Certified or accredited |
| Suspended | Paused |

Use the eye icon to view the detail and progress of an assignment, the pencil to edit it and the bin to delete it after a confirmation. Users with export rights can also export the list.
                `
            },
            {
                id: 'me-capture',
                title: 'Logging Progress',
                readTime: 4,
                content: `
**Log Progress** is where facility staff record how each indicator is going. The indicators are grouped by section, and each has its own card.

**Counters and filters:** The page shows how many indicators are logged, met and not met. Filter by section, or by logged, not logged, met or not met.

**On each indicator card:**

| Field | What to record |
|---|---|
| Status | Not Started, In Progress, Partially Met, Met, Not Met, Completed, Not Applicable or Delayed |
| Progress Narrative | What was done this period |
| Challenges / Barriers | Any obstacles |
| Evidence Description | What evidence exists, such as a document, record or observation |
| Evidence Date | When the evidence was created |
| Reference / Document Number | A file name, folder path or register number |
| Gap Identified | What gap was found |
| Corrective Action | The action being taken |
| Action Status, Action Owner, Deadline | Who is responsible and by when |
| Lessons Learned | What others could learn from this |

Click **Save** on the card to store your entry. A record that has been checked by a verifier shows a verified mark.

**Tip:** Record the evidence while you log progress. Records without evidence are harder to verify.
                `
            },
            {
                id: 'me-verify',
                title: 'Verifying Progress',
                readTime: 4,
                content: `
**Verify Progress** is where an assessor checks what facilities have logged. The list opens on records that are **pending** verification. You can also filter by framework, period and indicator status.

**Checking one record:**
1. Open the record with the view and verify icon
2. Read the status, narrative and evidence
3. Add notes about your decision
4. Click **Verify and Approve**, or **Reject - Return for Revision**

A verified or rejected record shows who decided and when.

**Quick actions:** The tick and cross icons on a row verify or reject a record straight away, without notes.

**Working in bulk:** Select several records and click **Verify Selected** or **Reject Selected**, then confirm. Check a sample first.

Rejected records go back to the facility to correct and save again.
                `
            },
            {
                id: 'me-dashboard',
                title: 'M&E Dashboard',
                readTime: 3,
                content: `
The **M&E Dashboard** shows how all active frameworks are progressing. Filter by framework and by region.

**Summary cards:** Active Frameworks, Total Assignments, Avg Completion, Indicators Tracked, Met Indicators, Pending Verification, Overdue Actions and Delayed Assignments.

**Overdue corrective actions:** A red alert lists actions that have passed their deadline, with the indicator, facility, action owner, days overdue and the action itself. These need immediate attention.

**Completion bands:**

| Completion | Band |
|---|---|
| 75% and above | On Track |
| 50% to 74% | At Risk |
| 25% to 49% | Off Track |
| Below 25% | Not Started |

The dashboard also shows a trend by reporting period, and a table of assignments with facility, progress, status, records pending verification and overdue actions.
                `
            },
            {
                id: 'me-sdg',
                title: 'SDG Health Progress Report',
                readTime: 3,
                content: `
The **SDG Health Progress Report** tracks Eswatini's progress against the SDG 3 targets and the NHSSP 2024-2028 indicators.

**To use it:**
1. Select the **reporting period**
2. Optionally select a **region**
3. Optionally filter by **SDG target** or by **status**

**What it shows:**
- **Section 1: SDG 3 Target Progress.** The indicators mapped to each SDG 3 target, with progress
- **NHSSP-specific indicators.** Indicators for governance, systems and financing that do not map directly to an SDG 3 target

Records verified by an M&E officer are marked as verified.

**Downloads:** Use **Download PDF** for the full report, or the Word option for an editable copy. Individual sections also have a PDF button.

**If you see a warning about no data:** facilities need to log progress in the M&E Tracker before this report can show actual achievement.
                `
            },
        ],
    },
    {
        id: 'moh-reports',
        icon: '\u{1F3DB}',
        title: 'MoH Reports',
        color: '#d46b08',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'moh-overview',
                title: 'MoH Reporting Overview',
                readTime: 3,
                content: `
**MoH Reporting** collects the periodic data each submitting unit (a programme, unit or department) reports to the Ministry, and turns it into a Technical Report.

| Step | Screen | Who does it |
|---|---|---|
| 1. Set up indicators, units and period assignments | Framework and Unit Setup | MoH reporting administrators |
| 2. Log progress on indicators | Log Progress (Indicators) | Unit staff |
| 3. Enter finance, HR, clinical and fleet data | Finance, HR, Clinical and Fleet | Unit staff |
| 4. Review and sign off the indicators | Review and Sign-off (Indicators) | Reviewers |
| 5. Download the report | Generate Report | Reporting officers |

Each screen only appears for users who have been given it.
                `
            },
            {
                id: 'moh-admin',
                title: 'Framework and Unit Setup',
                readTime: 4,
                content: `
**Framework and Unit Setup** has three tabs.

**1. Indicator Catalog:** Choose a section, then add the indicators it contains.

| Field | Notes |
|---|---|
| Code | For example KPI.1 |
| Title | Required. For example PrEP initiations this quarter |
| Type | Defaults to KPI |
| Measurement Unit | Defaults to percentage |
| Frequency | Once, monthly, quarterly, biannual or annual. Defaults to quarterly |
| Responsible Role / Unit | Who reports it |
| Data Source | For example HRIS or eLMIS |

**2. Submitting Units:** Register each programme, unit or department that reports. A **name** and a short, **unique code** are required, and a unit has a category of program, unit or department. **Warning:** deleting a unit also removes its report assignments and its submitted data.

**3. Period Assignments:** Choose a reporting period, select the units that must report in it, and assign them. You can remove a unit's assignment for a period after a confirmation.

Deleting an indicator also asks for a confirmation.
                `
            },
            {
                id: 'moh-data',
                title: 'Finance, HR, Clinical and Fleet Data',
                readTime: 5,
                content: `
This screen collects the numbers that sit outside the indicators. Select the **submitting unit** and the **reporting period** first. Each tab shows a badge with the number of lines already entered.

**Finance:**

| Field | Notes |
|---|---|
| Budget Line Description | Required |
| Budget Code | The budget code |
| Status | Status of the line |
| Annual Budget (E) | In Emalangeni |
| Cumulative Released (E) | Funds released so far |
| Spent/Committed (E) | Funds spent or committed |

The table shows the budget line, annual budget, spent or committed, absorption and status.

**HR Establishment:** Cadre / Post Title (required), Established Posts, Filled Posts, Vacant Posts and Notes. Vacant posts are worked out automatically, and you can override the figure if needed. The table shows the vacancy rate.

**Clinical Services:** Facility Name, Catchment Population, Bed Capacity, OPD Visits, Admissions, Deliveries, Inpatient Days, Deaths, and OPD Visits for the same period in the prior year.

**Fleet:** Enter each vehicle category. The table shows the total, active and grounded vehicles.

Entries can be edited, and deleted after a confirmation. These numbers go into the Technical Report.
                `
            },
            {
                id: 'moh-indicators',
                title: 'Logging and Reviewing Indicators',
                readTime: 2,
                content: `
**Log Progress (Indicators)** and **Review and Sign-off (Indicators)** use the same screens as the M&E Tracker.

- To record progress on an indicator, follow the guide **Logging Progress**
- To check and approve records, follow the guide **Verifying Progress**

Only the indicators assigned to your unit for the selected period appear.
                `
            },
            {
                id: 'moh-generate',
                title: 'Generating the Technical Report',
                readTime: 2,
                content: `
Go to **Generate Report** to download the Technical Report as an editable Word document.

1. Select the **submitting unit**
2. Select the **reporting period**
3. Click **Download Report (Word)**

The report covers one unit: every indicator, together with the finance, HR, clinical and fleet data entered for that period. The file is named from the unit code and period, for example moh_report_unitcode_period.docx.

**If generation fails:** check that the unit has data for the selected period, and that its finance, HR, clinical and fleet entries are complete.
                `
            },
        ],
    },
    {
        id: 'troubleshooting',
        icon: '🔧',
        title: 'Troubleshooting',
        color: '#f5222d',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'tr-dashboard',
                title: 'Dashboard Shows No Data',
                readTime: 2,
                content: `
**Problem:** Charts and KPI cards show 0 or "No data."

**Check these:**

1. **Period filter** — The dashboard defaults to the current period. If no assessments exist within that period's date range, nothing will show. Try clearing the period filter or selecting a different one.

2. **Assessment status** — Only Submitted or Approved assessments appear in dashboards. Drafts are excluded.

3. **Date range** — The date range must overlap with your assessment dates.

4. **Scores not calculated** — If assessments were bulk-imported without recalculating scores, the overall_percentage field may be 0. Contact your administrator to recalculate scores.
                `
            },
            {
                id: 'tr-report',
                title: 'Report Shows "Not Assessed"',
                readTime: 2,
                content: `
**Problem:** The Facility Report shows data for only one clause; others show "Not assessed."

**Cause:** The report was opened from a single-clause assessment entry rather than the grouped period entry.

**Fix:**
1. Open the Facility Report modal
2. In the assessment dropdown, look for an entry showing "(10 clauses)" — e.g. "FY2025/26 — 81.3% · 15 Jul 25 (10 clauses)"
3. Select that grouped entry

If the dropdown only shows individual clause entries, the assessments have different period labels. Ensure all assessments for the same facility and audit cycle share the same period label.
                `
            },
            {
                id: 'tr-permissions',
                title: 'Cannot See a Page',
                readTime: 2,
                content: `
**Problem:** A navigation item is missing or you see "Access denied."

**Steps to resolve:**
1. Contact your administrator
2. They should go to **Settings → Access Control**
3. Click your role card in the Role Permissions tab
4. Check whether the relevant module is enabled
5. If not, enable it and save
6. **Log out and back in** — permission changes only apply on fresh login

**If you're an administrator:**
- Go to Settings → Access Control → Role Permissions
- Click the user's role
- Enable the missing module
- Save and ask the user to re-login
                `
            },
            {
                id: 'tr-import',
                title: 'KoboCollect Import Errors',
                readTime: 2,
                content: `
**"me_id = 0" or missing responses after import:**
The clause was not selected before importing. KoboCollect exports use short ME codes (e.g. \`1.1\`) — you must select the clause first so the system knows to add the prefix (e.g. \`AACC.1.1\`).

**Duplicate entry error:**
The same assessment has already been imported. Check the assessments list before re-importing.

**Wrong scores after import:**
Verify that all MEs in the import file have valid fulfillment values (Met, Partially Met, Not Met, Not Applicable, or Not Checked). Any other value is ignored during scoring.
                `
            },
        ]
    },
];

const SECTIONS_ADMIN = [
    {
        id: 'admin-access',
        icon: '🔐',
        title: 'Access Control',
        color: '#722ed1',
        roles: ['admin'],
        topics: [
            {
                id: 'ac-overview',
                title: 'Managing Role Permissions',
                readTime: 4,
                graphic: <SettingsMockup />,
                content: `
**Settings → Access Control** is where you manage what each role can see and do on the platform.

**Role Permissions tab:**
- Shows all roles as cards, each with a badge showing how many modules are enabled
- Click any role to open the permissions modal
- The modal shows every active module grouped by category
- Toggle each module on or off with a switch
- Use **Enable All** / **Disable All** for quick bulk changes
- Click **Save Permissions** — changes take effect on users' next login

**Important:** Permission changes do not affect currently logged-in sessions. Users must log out and back in.

**How permissions flow:**
1. Module added to the modules table with a unique key
2. Role is given that module via the Permissions modal → saved to roles.app_modules
3. On login, the server sends the user's enabled modules
4. Frontend reads from localStorage via util.getModules()
5. Nav items check modules['module_key'] === 1 to show/hide
6. Backend checkAccess('module_key') enforces it on every API call
                `
            },
            {
                id: 'ac-modules',
                title: 'Module Management',
                readTime: 3,
                content: `
The **Module Management** tab shows the complete modules table — all available system modules.

**Creating a new module:**
1. Click **New Module**
2. Fill in:
   - **Key** — unique lowercase identifier (e.g. \`qoc_reports\`) — cannot be changed after creation
   - **Display Name** — human-readable label shown in the permissions grid
   - **Group** — which category it appears under (Administration, Quality of Care, etc.)
   - **Applies To** — CLIENT (facility users only), ADMIN, or BOTH
   - **National Level Only** — restricts to national-level users
   - **Active** — inactive modules are hidden from the permissions grid but not deleted
3. Save the module
4. Go back to Role Permissions and enable it for the appropriate roles

**Editing modules:**
- Edit display name, group, or visibility
- Built-in modules (id ≤ 221) are protected from deletion
- Custom modules can be deleted if no longer needed

**Best practice roles:**
| Role | Permissions |
|---|---|
| Facility Assessor | qoc_assess |
| Approval Officer | qoc_approve |
| National Analyst | qoc_analytics, qoc_executive_dash, qoc_facility_report |
| QoC Administrator | qoc_standards, qoc_periods, qoc_schedules, qoc_kpis |
| Facility Manager | manage_facilities, qoc_facility_profile |
                `
            },
        ]
    },
    {
        id: 'admin-periods',
        icon: '📅',
        title: 'Assessment Periods',
        color: '#f59e0b',
        roles: ['admin'],
        topics: [
            {
                id: 'per-create',
                title: 'Creating and Managing Periods',
                readTime: 3,
                content: `
Periods define the audit cycle window, for example FY2025/26: 1 April 2025 – 31 March 2026.

Go to **Settings → Assessment Periods** to:
1. Create a new period with a label, start date, and end date
2. Mark one period as **Current** — this becomes the default in all dashboards and the public survey form
3. Mark periods as **Active** to include them in filter dropdowns

**Best practice:**
- Always set exactly one period as Current
- Dashboards automatically default to the current period's date range on first load
- Keep previous periods Active so they remain available in filters and Period Comparison

**Period Comparison:** Once you have 2+ active periods with assessment data, the Executive Dashboard's Period Comparison section can compare performance across up to 5 cycles.
                `
            },
            {
                id: 'per-schedules',
                title: 'Audit Schedules',
                readTime: 3,
                content: `
Audit Schedules define when each facility is planned to be audited within a period.

Go to **Settings → Audit Schedules** to:
1. Create a new schedule by selecting facility, period, and planned date
2. Assign a lead assessor from the user list
3. Track status: Planned → In Progress → Completed / Overdue

**How schedules link to assessments:**
When an assessor creates an assessment, they can link it to an existing schedule. Once an assessment is submitted, the schedule status automatically updates to "Completed."

Overdue schedules (planned date passed, no assessment) appear in the Executive Dashboard's Schedules section and KPI cards.

**Bulk schedule creation:**
Select multiple facilities and a date range to auto-generate one schedule per facility — useful at the start of each audit period.
                `
            },
        ]
    },
    {
        id: 'admin-users',
        icon: '👥',
        title: 'Users & Facilities',
        color: '#60a5fa',
        roles: ['admin'],
        topics: [
            {
                id: 'usr-manage',
                title: 'Managing Users',
                readTime: 3,
                content: `
Go to **Administration → Roles & Users** (requires \`manage_roles_users\` permission).

**Adding a new user:**
1. Click **Add User**
2. Set first name, last name, email, mobile, and username
3. Set a password
4. Assign a **Role** — the role controls which modules the user can access
5. Set scope: National User (no specific facility) or Facility User (assigned to one facility)
6. Set status to Active and save

**User types:**
- **National User** — Can see data across all facilities (assigned to facility ID 137 internally)
- **Facility User** — Sees data for their assigned facility only

**Editing users:**
Click the edit icon on any user row. You can update all fields except username. To change a password, enter a new value in the password field — leave blank to keep the current password.

**Best practice:** Create separate roles per function. Never give every user the administrative modules (Access Control, Standards Setup, Periods, Users).
                `
            },
            {
                id: 'fac-manage',
                title: 'Managing Facilities',
                readTime: 3,
                content: `
Go to **Administration → Facilities** (requires \`manage_facilities\` permission).

**Facility record key fields:**
| Field | Description |
|---|---|
| Business Name | Full official facility name |
| Site Type / Level | e.g. Clinic without Maternity, Regional Hospital |
| Located Region | Hhohho, Manzini, Lubombo, or Shiselweni |
| Facility Code | Unique identifier |
| Status | Active / Inactive |

**Adding facilities from HIE (recommended):**
Use **Settings → HIE Facility Sync** to import directly from the national Health Information Exchange. This ensures alignment with the national registry.

**Adding manually:**
1. Click **Add Facility**
2. Fill in Business Name, Site Type, and Region (all mandatory)
3. Set status to Active and save

**Why accuracy matters:** Facility name, region, and level appear in all exports, dashboards, and dashboard filters. The "Level" dashboard filter uses the Site Type field specifically.
                `
            },
        ]
    },
    {
        id: 'admin-hie',
        icon: '🔄',
        title: 'HIE Facility Sync',
        color: '#0ea5e9',
        roles: ['admin'],
        topics: [
            {
                id: 'hie-setup',
                title: 'Setting Up HIE Sync',
                readTime: 4,
                content: `
**Settings → HIE Facility Sync** connects the platform to the national Health Information Exchange (HIE) using HL7 FHIR R4.

**One-time setup:**
1. Enter the **FHIR Base URL** — e.g. \`https://hie.health.gov.sz/fhir\`
2. Select **Auth Type** — Bearer Token, API Key, or None
3. Paste the **Auth Token** if required
4. Choose **Sync Mode**:
   - *Add new + Update changed* — adds new facilities AND updates existing ones when HIE data has changed
   - *Add new only* — only adds facilities that don't yet exist (never overwrites local data)
5. Click **Save Configuration**
6. Click **Test Connection** — should show green "Connected"

**FHIR field mapping:**
| FHIR field | QoC field |
|---|---|
| Location.id | hie_id (internal link) |
| Location.name | Facility name |
| Location.address.district | Region |
| Location.type | Site type |
| Location.telecom[phone] | Phone |
                `
            },
            {
                id: 'hie-sync',
                title: 'Running a Sync',
                readTime: 3,
                content: `
1. Click **Fetch from HIE & Preview** — downloads all Location resources and shows a diff table:
   - 🟢 **New** — exists in HIE but not in QoC
   - 🔵 **Update** — exists in both but fields have changed
   - **No Change** — already up to date
   - ⚠ **Blank** — HIE didn't populate that field (common for region or type)

2. Review the table — check the Action and Changes columns before proceeding

3. Click **Sync Now** → confirm in the modal → sync applies

**Blank fields:**
If HIE doesn't have region or facility type, the facility is still imported (with name and HIE ID). Edit blank fields manually in Administration → Facilities after sync.

**Facilities are never deleted by sync.** Only additions and updates are applied.

**Login badge:**
If new or changed facilities are detected in HIE since your last login, a red badge appears on the HIE Facility Sync menu item. The badge is cached for 6 hours.
                `
            },
        ]
    },
    {
        id: 'admin-standards',
        icon: '📐',
        title: 'Standards Setup',
        color: '#1890ff',
        roles: ['admin'],
        topics: [
            {
                id: 'std-manage',
                title: 'Managing Standards & MEs',
                readTime: 3,
                content: `
Go to **Quality of Care → Standards Setup** (requires the \`qoc_standards\` module).

The standards hierarchy is:
**Standard → Clause → Sub-Standard → Measurable Element (ME)**

**What you can do:**
- View all clauses, standards, sub-standards, and measurable elements
- Mark specific MEs as **Key Critical Standards (KCS)**
- Update ME text or add new MEs
- Manage the standards hierarchy

**⚠ Warning:** Changes to MEs affect all future assessments. Existing responses are not retroactively changed. Consult the team before modifying active MEs.

**KPI Targets:**
Go to **National Oversight → KPI Targets** to set expected performance levels by clause and period. These appear in the Executive Dashboard's KPI vs Actuals section (Section 7) as gauge charts.
                `
            },
        ]
    },
];

// ─── Render content ───────────────────────────────────────────────────────────
// Dynamic manual: which module keys unlock which topics (topics not listed here are visible to everyone)
const TOPIC_MODULES = {
    'as-create': ['qoc_assess'], 'as-responses': ['qoc_assess'], 'as-submit': ['qoc_assess'],
    'as-import': ['qoc_import'],
    'ap-review': ['qoc_approve'], 'ap-bulk': ['qoc_approve'],
    'db-executive': ['qoc_executive_dash'], 'db-filters': ['qoc_executive_dash'], 'db-period': ['qoc_executive_dash'],
    'rp-facility': ['qoc_facility_report'], 'rp-excel': ['qoc_facility_report'], 'rp-card-export': ['qoc_executive_dash'],
    'sv-public': ['qoc_facility_coords', 'qoc_survey_dash'], 'sv-manage': ['qoc_survey_dash'],
    'ays-overview': ['ays_submit', 'ays_admin', 'ays_analytics'],
    'ays-submit': ['ays_submit'], 'ays-admin': ['ays_admin'],
    'ays-dashboard': ['ays_admin', 'ays_analytics'], 'ays-analytics': ['ays_admin', 'ays_analytics'],
    'enhea-overview': ['prog_assess', 'prog_admin', 'qoc_nominations', 'ha_judge'],
    'enhea-nominations': ['qoc_nominations'], 'enhea-programs': ['prog_assess'],
    'enhea-approval': ['prog_admin', 'prog_assess'], 'enhea-report': ['prog_assess', 'prog_admin'],
    'enhea-judge': ['ha_judge'], 'enhea-dashboard': ['prog_admin'],
    'tr-dashboard': ['qoc_executive_dash'], 'tr-report': ['qoc_facility_report'], 'tr-import': ['qoc_import'],
    'ac-overview': ['qoc_access_control'], 'ac-modules': ['qoc_access_control', 'qoc_nav_manager'],
    'per-create': ['qoc_periods'], 'per-schedules': ['qoc_schedules'],
    'usr-manage': ['manage_roles_users'], 'fac-manage': ['manage_facilities'],
    'hie-setup': ['qoc_hie_sync'], 'hie-sync': ['qoc_hie_sync'],
    'std-manage': ['qoc_standards', 'qoc_kpis'],
    'me-overview': ['me_view', 'me_admin', 'me_capture', 'me_verify', 'me_assign', 'me_sdg_report'],
    'me-frameworks': ['me_admin'], 'me-assign': ['me_assign'], 'me-capture': ['me_capture'], 'me-verify': ['me_verify'],
    'me-dashboard': ['me_view'], 'me-sdg': ['me_sdg_report'],
    'moh-overview': ['moh_report_capture', 'moh_report_verify', 'moh_report_assign', 'moh_report_admin', 'moh_report_output'],
    'moh-admin': ['moh_report_admin', 'moh_report_assign'], 'moh-data': ['moh_report_capture'],
    'moh-indicators': ['moh_report_capture', 'moh_report_verify'], 'moh-generate': ['moh_report_output'],
    'fp-profile': ['qoc_facility_profile'], 'fp-dashboard': ['qoc_facility_profile'], 'team-registry': ['qoc_team'],
    'ov-compliance': ['qoc_compliance'], 'ov-qi': ['qoc_quality_index'], 'ov-smt': ['smt_dashboard'], 'ov-trail': ['qoc_activity_trail'],
    'ipc-assess': ['qoc_assess'], 'ipc-analytics': ['qoc_view'], 'cap-manage': ['qoc_cap'], 'cap-commitment': ['qoc_cap'],
    'svb-overview': ['survey_admin'], 'svb-build': ['survey_admin'], 'svb-import': ['survey_admin'], 'svb-collect': ['survey_admin'],
    'svb-collab': ['survey_admin'], 'svb-progress': ['survey_admin'], 'svb-templates': ['survey_admin'],
};
// Modules that exist but have no guide yet (shown as "Guides coming soon" until a topic is mapped to them above)
const MODULE_NAMES = {
    qoc_cap: 'Corrective Actions', qoc_team: 'Audit Team', qoc_facility_profile: 'Facility Profile',
    qoc_compliance: 'Compliance', qoc_quality_index: 'Quality Index', qoc_analytics: 'Analytics',
    qoc_activity_trail: 'Activity Trail', smt_dashboard: 'SMT Alerts', qoc_view: 'IPCAF Analytics',
    
    me_view: 'M&E Dashboard', me_admin: 'M&E Frameworks', me_capture: 'M&E Log Progress',
    me_verify: 'M&E Verify Progress', me_assign: 'M&E Assignments', me_sdg_report: 'SDG Progress Report',
    moh_report_view: 'MOH Submission Dashboard', moh_report_capture: 'MOH Report Capture',
    moh_report_verify: 'MOH Review and Sign-off', moh_report_assign: 'MOH Unit Assignments',
    moh_report_admin: 'MOH Framework Setup', moh_report_output: 'MOH Report Generation',
};
const hasMod = (mods, k) => Number(mods && mods[k]) === 1;
const topicVisible = (t, mods) => { const need = TOPIC_MODULES[t.id]; return !need || need.some(k => hasMod(mods, k)); };
function buildSections(mods, showAll) {
    const all = [...SECTIONS_USER, ...SECTIONS_ADMIN];
    if (showAll) return all;
    const out = all.map(s => ({ ...s, topics: s.topics.filter(t => topicVisible(t, mods)) })).filter(s => s.topics.length > 0);
    return out.length ? out : all.slice(0, 1);
}
function documentedGaps(mods) {
    const covered = new Set([].concat(...Object.values(TOPIC_MODULES)));
    return Object.keys(MODULE_NAMES).filter(k => hasMod(mods, k) && !covered.has(k)).map(k => MODULE_NAMES[k]);
}
function renderContent(text) {
    const lines = text.trim().split('\n');
    const elements = [];
    let i = 0;
    let tableRows = [];
    let inTable = false;

    const flushTable = () => {
        if (tableRows.length < 2) { tableRows = []; inTable = false; return; }
        const headers = tableRows[0].split('|').map(h => h.trim()).filter(Boolean);
        const rows = tableRows.slice(2).map(r => r.split('|').map(c => c.trim()).filter(Boolean));
        elements.push(
            <div key={`table-${i}`} style={{ overflowX: 'auto', marginBottom: 20 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                        <tr style={{ background: '#f0f5ff' }}>
                            {headers.map((h, hi) => (
                                <th key={hi} style={{ padding: '10px 14px', textAlign: 'left', borderBottom: '2px solid #d6e4ff', fontWeight: 700, color: '#1a1a2e', fontSize: 14 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri} style={{ background: ri % 2 ? '#fafbff' : '#fff' }}>
                                {row.map((cell, ci) => (
                                    <td key={ci} style={{ padding: '9px 14px', borderBottom: '1px solid #e8f0fe', fontSize: 14 }}>
                                        {renderInline(cell)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
        tableRows = []; inTable = false;
    };

    while (i < lines.length) {
        const line = lines[i];
        if (line.startsWith('|')) { inTable = true; tableRows.push(line); i++; continue; }
        if (inTable) flushTable();

        if (line.startsWith('**') && line.endsWith('**') && !line.slice(2, -2).includes('**')) {
            elements.push(<div key={i} style={{ fontWeight: 800, fontSize: 16, color: '#0f172a', marginTop: 22, marginBottom: 8 }}>{line.slice(2, -2)}</div>);
        } else if (line.startsWith('```')) {
            const codeLines = [];
            i++;
            while (i < lines.length && !lines[i].startsWith('```')) { codeLines.push(lines[i]); i++; }
            elements.push(
                <pre key={i} style={{ background: '#0f172a', color: '#e2e8f0', padding: '16px 20px', borderRadius: 10, fontSize: 13, overflowX: 'auto', marginBottom: 20, lineHeight: 1.8 }}>
                    <code>{codeLines.join('\n')}</code>
                </pre>
            );
        } else if (line.startsWith('- ')) {
            const items = [line.slice(2)];
            i++;
            while (i < lines.length && lines[i].startsWith('- ')) { items.push(lines[i].slice(2)); i++; }
            elements.push(
                <ul key={'ul' + i} style={{ paddingLeft: 22, marginBottom: 16 }}>
                    {items.map((item, ii) => <li key={ii} style={{ marginBottom: 6, fontSize: 15, color: '#334155', lineHeight: 1.8 }}>{renderInline(item)}</li>)}
                </ul>
            );
            continue;
        } else if (line.match(/^\d+\. /)) {
            const items = [line.replace(/^\d+\. /, '')];
            i++;
            while (i < lines.length && lines[i].match(/^\d+\. /)) { items.push(lines[i].replace(/^\d+\. /, '')); i++; }
            elements.push(
                <ol key={'ol' + i} style={{ paddingLeft: 22, marginBottom: 16 }}>
                    {items.map((item, ii) => <li key={ii} style={{ marginBottom: 8, fontSize: 15, color: '#334155', lineHeight: 1.8 }}>{renderInline(item)}</li>)}
                </ol>
            );
            continue;
        } else if (line === '') {
            elements.push(<div key={i} style={{ height: 10 }} />);
        } else {
            elements.push(<p key={i} style={{ fontSize: 15, color: '#334155', lineHeight: 1.9, marginBottom: 12 }}>{renderInline(line)}</p>);
        }
        i++;
    }
    if (inTable) flushTable();
    return elements; 
}

function renderInline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} style={{ color: '#0f172a', fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
        if (part.startsWith('`') && part.endsWith('`')) return <code key={i} style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 13, fontFamily: 'monospace', color: '#0369a1' }}>{part.slice(1, -1)}</code>;
        return part;
    });
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function QoCUserManual() {
    const [role, setRole] = useState('user');
    const [activeSection, setActiveSection] = useState('getting-started');
    const [activeTopic, setActiveTopic] = useState('gs-overview');
    const [search, setSearch] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [tourMode, setTourMode]       = useState(false);
    const [tourStep, setTourStep]        = useState(0);
    const [voiceOn, setVoiceOn]          = useState(true);
    const [speaking, setSpeaking]        = useState(false);
    const [completed, setCompleted] = useState(() => { try { return JSON.parse(localStorage.getItem('qoc_manual_completed_v1') || '{}') || {}; } catch (e) { return {}; } });
    const contentRef = useRef(null);
    const mods = (util.getModules && util.getModules()) || {};
    const canSeeAll = hasMod(mods, 'qoc_access_control') || hasMod(mods, 'manage_roles_users');
    useEffect(() => { try { localStorage.setItem('qoc_manual_completed_v1', JSON.stringify(completed)); } catch (e) {} }, [completed]);

    const SECTIONS = buildSections(mods, canSeeAll && role === 'admin');
    const undocumented = (canSeeAll && role === 'admin') ? [] : documentedGaps(mods);
    const allTopics = SECTIONS.flatMap(s => s.topics.map(t => ({ ...t, sectionId: s.id, sectionTitle: s.title, sectionColor: s.color, sectionIcon: s.icon })));

    const searchResults = search.trim().length > 1
        ? allTopics.filter(t => t.title.toLowerCase().includes(search.toLowerCase()) || t.content.toLowerCase().includes(search.toLowerCase()))
        : [];

    const currentSection = SECTIONS.find(s => s.id === activeSection) || SECTIONS[0];
    const currentTopic = allTopics.find(t => t.id === activeTopic) || allTopics[0];

    const totalTopics = allTopics.length;
    const completedCount = allTopics.filter(t => completed[t.id]).length;
    const progressPct = Math.round((completedCount / totalTopics) * 100);

    const navigate = (sectionId, topicId) => {
        setActiveSection(sectionId);
        setActiveTopic(topicId);
        setSearch('');
        if (contentRef.current) contentRef.current.scrollTop = 0;
    };

    const markDone = (topicId) => {
        setCompleted(prev => ({ ...prev, [topicId]: true }));
    };

    // Auto-set first section when role changes
    useEffect(() => {
        const sections = SECTIONS;
        setActiveSection(sections[0].id);
        setActiveTopic(sections[0].topics[0].id);
    }, [role, canSeeAll]); // eslint-disable-line

    const totalReadTime = allTopics.reduce((a, t) => a + (t.readTime || 3), 0);

    return (
        <div className="page-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="page-head uc d-flex" style={{ background: 'linear-gradient(135deg,#0f172a,#1e3a5f)', padding: '14px 24px', flexShrink: 0 }}>
                <div className="my-auto" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <BookOutlined style={{ fontSize: 22, color: '#60a5fa' }} />
                    <div>
                        <h2 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 800 }}>User Manual</h2>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Eswatini QoC Platform — Self-Paced Learning Guide</div>
                    </div>
                </div>
                <div className="my-auto ml-auto" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {/* Role toggle */}
                    <div style={{ display: canSeeAll ? 'flex' : 'none', background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 3, gap: 3 }}>
                        {Object.entries(ROLES).map(([key, r]) => (
                            <div key={key} onClick={() => setRole(key)}
                                style={{
                                    padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                    background: role === key ? '#fff' : 'transparent',
                                    color: role === key ? r.color : 'rgba(255,255,255,0.6)',
                                    transition: 'all 0.15s',
                                }}>
                                {r.icon} {r.label}
                            </div>
                        ))}
                    </div>
                    <Input
                        prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="Search manual…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ width: 200, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff' }}
                        allowClear
                    />
                    <button onClick={() => {
                        const el = document.getElementById('qoc-manual-print-area');
                        if (!el) { window.print(); return; }
                        const win = window.open('', '_blank');
                        win.document.write('<html><head><title>QoC Platform User Manual</title><style>body{font-family:Arial,sans-serif;font-size:13px;line-height:1.6;color:#111;padding:20px;max-width:900px;margin:0 auto}h1,h2,h3{color:#0d2137}@media print{button{display:none}}</style></head><body>');
                        win.document.write('<h1>QoC Platform - User Manual</h1>');
                        win.document.write(el.innerHTML);
                        win.document.write('</body></html>');
                        win.document.close();
                        setTimeout(() => win.print(), 500);
                    }} style={{ cursor:'pointer', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', color:'rgba(255,255,255,0.8)', borderRadius:6, padding:'5px 12px', fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                        🖨️ Print PDF
                    </button>
                    <button onClick={() => { setTourMode(true); setTourStep(0); setTimeout(() => { if(voiceOn && window.speechSynthesis){ const u=new SpeechSynthesisUtterance('Welcome to the QoC Platform guided tour.'); u.lang='en-US'; window.speechSynthesis.speak(u); } }, 300); }} style={{ cursor:'pointer', background:'#1d4ed8', border:'none', color:'#fff', borderRadius:6, padding:'5px 12px', fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                        ▶ Guided Tour
                    </button>
                    <span onClick={() => setSidebarOpen(v => !v)} style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: 18, padding: '4px 8px' }}>
                        {sidebarOpen ? <CloseOutlined /> : <MenuOutlined />}
                    </span>
                </div>
            </div>

            {/* Progress bar */}
            <div style={{ background: '#1e293b', padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
                <div style={{ flex: 1 }}>
                    <Progress percent={progressPct} showInfo={false} strokeColor="#60a5fa" trailColor="rgba(255,255,255,0.1)" size="small" style={{ margin: 0 }} />
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
                    {completedCount} of {totalTopics} topics · ~{totalReadTime} min read
                </div>
            </div>

            {/* Search overlay */}
            {search.trim().length > 1 && (
                <div style={{ position: 'absolute', top: 110, right: 24, zIndex: 999, width: 360, background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e8f0fe', maxHeight: 420, overflowY: 'auto' }}>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', fontSize: 12, color: '#888', fontWeight: 600 }}>
                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{search}"
                    </div>
                    {searchResults.length === 0
                        ? <div style={{ padding: 24, textAlign: 'center', color: '#aaa', fontSize: 14 }}>No results found</div>
                        : searchResults.map(t => (
                            <div key={t.id} onClick={() => navigate(t.sectionId, t.id)}
                                style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8faff'}
                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{t.title}</div>
                                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                                    <span style={{ color: t.sectionColor }}>{t.sectionIcon} {t.sectionTitle}</span>
                                    {completed[t.id] && <span style={{ marginLeft: 8, color: '#52c41a' }}>✓ Done</span>}
                                </div>
                            </div>
                        ))
                    }
                </div>
            )}

            {/* Body */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: '#f8faff' }}>
                {/* Sidebar */}
                {sidebarOpen && (
                    <div style={{ width: 272, flexShrink: 0, background: '#fff', borderRight: '1px solid #e8f0fe', overflowY: 'auto', padding: '16px 0' }}>
                        {/* Role badge */}
                        <div style={{ margin: '0 16px 16px', padding: '10px 14px', background: `${ROLES[role].color}10`, borderRadius: 8, border: `1px solid ${ROLES[role].color}30` }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: ROLES[role].color }}>{ROLES[role].icon} {ROLES[role].label}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{ROLES[role].desc}</div>
                        </div>
                        {SECTIONS.map(section => (
                            <div key={section.id} style={{ marginBottom: 4 }}>
                                <div onClick={() => navigate(section.id, section.topics[0].id)}
                                    style={{
                                        padding: '9px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                                        background: activeSection === section.id ? `${section.color}12` : 'transparent',
                                        borderLeft: activeSection === section.id ? `3px solid ${section.color}` : '3px solid transparent',
                                        transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => { if (activeSection !== section.id) e.currentTarget.style.background = '#f8faff'; }}
                                    onMouseLeave={e => { if (activeSection !== section.id) e.currentTarget.style.background = 'transparent'; }}>
                                    <span style={{ fontSize: 16 }}>{section.icon}</span>
                                    <span style={{ fontSize: 13, fontWeight: activeSection === section.id ? 700 : 500, color: activeSection === section.id ? section.color : '#475569', flex: 1 }}>
                                        {section.title}
                                    </span>
                                    {section.roles?.includes('admin') && !section.roles?.includes('user') && (
                                        <Tag style={{ fontSize: 9, padding: '0 4px', border: 'none', background: '#722ed115', color: '#722ed1' }}>Admin</Tag>
                                    )}
                                    <Badge count={section.topics.filter(t => completed[t.id]).length} style={{ background: '#52c41a', fontSize: 10, height: 16, minWidth: 16, lineHeight: '16px' }}
                                        showZero={false} />
                                </div>
                                {activeSection === section.id && (
                                    <div style={{ paddingLeft: 8, marginTop: 2 }}>
                                        {section.topics.map(topic => (
                                            <div key={topic.id}
                                                onClick={() => { setActiveTopic(topic.id); if (contentRef.current) contentRef.current.scrollTop = 0; }}
                                                style={{
                                                    padding: '7px 20px 7px 28px', cursor: 'pointer', fontSize: 13,
                                                    color: activeTopic === topic.id ? section.color : '#64748b',
                                                    fontWeight: activeTopic === topic.id ? 700 : 400,
                                                    background: activeTopic === topic.id ? `${section.color}10` : 'transparent',
                                                    borderRadius: 4,
                                                    borderLeft: activeTopic === topic.id ? `2px solid ${section.color}` : '2px solid transparent',
                                                    transition: 'all 0.1s', display: 'flex', alignItems: 'center', gap: 6,
                                                }}
                                                onMouseEnter={e => { if (activeTopic !== topic.id) e.currentTarget.style.background = '#f1f5f9'; }}
                                                onMouseLeave={e => { if (activeTopic !== topic.id) e.currentTarget.style.background = 'transparent'; }}>
                                                {completed[topic.id]
                                                    ? <CheckCircleFilled style={{ fontSize: 12, color: '#52c41a', flexShrink: 0 }} />
                                                    : <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: activeTopic === topic.id ? section.color : '#cbd5e1', display: 'inline-block' }} />
                                                }
                                                <span style={{ flex: 1 }}>{topic.title}</span>
                                                {topic.readTime && <span style={{ fontSize: 10, color: '#94a3b8' }}>{topic.readTime}m</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {undocumented.length > 0 && (
                            <div style={{ margin: '16px 16px 0', padding: '10px 12px', background: '#fffbe6', borderRadius: 8, border: '1px solid #ffe58f' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#ad6800', marginBottom: 4 }}>Guides coming soon</div>
                                <div style={{ fontSize: 11, color: '#876800', lineHeight: 1.5 }}>{undocumented.join(', ')}</div>
                            </div>
                        )}
                        <div style={{ margin: '24px 16px 8px', padding: '12px', background: '#f8faff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>Eswatini Ministry of Health</div>
                            <div style={{ fontSize: 11, color: '#cbd5e1' }}>National Quality Management Programme</div>
                        </div>
                    </div>
                )}

                {/* Content */}
                <div ref={contentRef} style={{ flex: 1, overflowY: 'auto', padding: '36px 48px' }}>
                    {currentTopic && currentSection && (
                        <>
                            {/* Breadcrumb */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24, fontSize: 13, color: '#94a3b8' }}>
                                <span style={{ color: currentSection.color, fontWeight: 600 }}>{currentSection.icon} {currentSection.title}</span>
                                <span>›</span>
                                <span style={{ color: '#475569' }}>{currentTopic.title}</span>
                                {currentTopic.readTime && (
                                    <span style={{ marginLeft: 'auto', background: '#f0f5ff', padding: '2px 10px', borderRadius: 20, fontSize: 12, color: '#1890ff' }}>
                                        ~{currentTopic.readTime} min read
                                    </span>
                                )}
                            </div>

                            {/* Title */}
                            <div style={{ marginBottom: 28, paddingBottom: 20, borderBottom: `2px solid ${currentSection.color}20` }}>
                                <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900, color: '#0f172a', lineHeight: 1.3 }}>{currentTopic.title}</h1>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                                    <Tag style={{ background: `${currentSection.color}15`, color: currentSection.color, border: 'none', fontWeight: 600, fontSize: 12 }}>
                                        {currentSection.icon} {currentSection.title}
                                    </Tag>
                                    {completed[currentTopic.id] && (
                                        <Tag style={{ background: '#f6ffed', color: '#52c41a', border: '1px solid #b7eb8f', fontSize: 12 }}>
                                            <CheckCircleFilled style={{ marginRight: 4 }} />Completed
                                        </Tag>
                                    )}
                                </div>
                            </div>

                            {/* Graphic */}
                            {currentTopic.graphic && (
                                <div style={{ marginBottom: 28 }}>
                                    {currentTopic.graphic}
                                    <div style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                                        UI illustration — {currentTopic.title}
                                    </div>
                                </div>
                            )}

                            {/* Content */}
                            <div style={{ maxWidth: 800 }}>
                                <div key={currentTopic.id}>{renderContent(currentTopic.content)}</div>
                            </div>

                            {/* Mark complete button */}
                            {!completed[currentTopic.id] && (
                                <div style={{ marginTop: 32, padding: '16px 20px', background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd', display: 'flex', alignItems: 'center', gap: 16, maxWidth: 800 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Done reading?</div>
                                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Mark this topic as complete to track your progress</div>
                                    </div>
                                    <div onClick={() => markDone(currentTopic.id)}
                                        style={{ cursor: 'pointer', background: '#0ea5e9', color: '#fff', padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        ✓ Mark Complete
                                    </div>
                                </div>
                            )}

                            {/* Prev / Next */}
                            <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: 12, maxWidth: 800 }}>
                                {(() => {
                                    const flat = SECTIONS.flatMap(s => s.topics.map(t => ({ ...t, sectionId: s.id, sectionColor: s.color })));
                                    const idx = flat.findIndex(t => t.id === activeTopic);
                                    const prev = flat[idx - 1];
                                    const next = flat[idx + 1];
                                    return (
                                        <>
                                            {prev
                                                ? <div onClick={() => navigate(prev.sectionId, prev.id)}
                                                    style={{ cursor: 'pointer', padding: '12px 18px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', maxWidth: '45%' }}
                                                    onMouseEnter={e => e.currentTarget.style.borderColor = prev.sectionColor}
                                                    onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                                                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 3 }}>← Previous</div>
                                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>{prev.title}</div>
                                                </div>
                                                : <div />
                                            }
                                            {next
                                                ? <div onClick={() => {
                                                    if (!completed[currentTopic.id]) markDone(currentTopic.id);
                                                    navigate(next.sectionId, next.id);
                                                }}
                                                    style={{ cursor: 'pointer', padding: '12px 18px', borderRadius: 10, border: `1px solid ${next.sectionColor || '#e2e8f0'}`, background: '#fff', maxWidth: '45%', textAlign: 'right' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#f8faff'}
                                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 3 }}>Next →</div>
                                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>{next.title}</div>
                                                </div>
                                                : <div style={{ padding: '12px 18px', borderRadius: 10, background: '#f6ffed', border: '1px solid #b7eb8f', textAlign: 'center' }}>
                                                    <div style={{ fontSize: 16 }}>🎉</div>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#52c41a' }}>You've completed the manual!</div>
                                                </div>
                                            }
                                        </>
                                    );
                                })()}
                            </div>
                        </>
                    )}
                </div>
            </div>
            {/* Guided Tour */}
            {tourMode && (() => {
                const allSteps = [
                    { t: 'Welcome', b: 'This guided tour walks you through the main features. Use Next and Back to navigate.' },
                    { t: 'Navigation', b: 'The left sidebar organises all modules. Your role controls what you see.' },
                    { m: ['qoc_assess'], t: 'Quality Assessments', b: 'Create facility assessments, score measurable elements, and submit for approval.' },
                    { m: ['qoc_approve'], t: 'Approval Workflow', b: 'Submitted assessments appear in the Approval Queue for review and approval.' },
                    { m: ['qoc_executive_dash'], t: 'Executive Dashboard', b: 'National performance at a glance - scores, trends, heatmaps, and KPIs.' },
                    { m: ['prog_assess', 'prog_admin', 'qoc_nominations', 'ha_judge'], t: 'Health Excellence Awards', b: 'Nominate facilities, assess programmes, coordinate judges, view rankings.' },
                    { m: ['ays_submit', 'ays_admin', 'ays_analytics'], t: 'AYS Scorecard', b: 'Peer Educators submit monthly patient feedback scorecards.' },
                    { t: 'End of Tour', b: 'You have completed the tour. Browse the manual sections for detailed guidance.' },
                ];
                const steps = allSteps.filter(st => !st.m || st.m.some(k => hasMod(mods, k)));
                const s = steps[Math.min(tourStep, steps.length-1)];
                const last = tourStep >= steps.length-1;
                const go = (i) => {
                    window.speechSynthesis?.cancel();
                    setTourStep(i);
                    if (voiceOn && window.speechSynthesis) {
                        setTimeout(() => {
                            const u = new SpeechSynthesisUtterance(steps[i].t + '. ' + steps[i].b);
                            u.lang='en-US'; u.rate=0.95;
                            const vs = window.speechSynthesis.getVoices();
                            const v = vs.find(v=>v.lang==='en-US')||vs[0];
                            if(v) u.voice=v;
                            u.onstart=()=>setSpeaking(true); u.onend=()=>setSpeaking(false);
                            window.speechSynthesis.speak(u);
                        }, 150);
                    }
                };
                return (
                    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}
                        onClick={()=>{window.speechSynthesis?.cancel();setTourMode(false);}}>
                        <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:16,padding:32,maxWidth:480,width:'92%',boxShadow:'0 24px 64px rgba(0,0,0,0.3)'}}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                    <span style={{fontSize:11,color:'#888',fontWeight:600}}>Step {tourStep+1} of {steps.length}</span>
                                    {speaking && <span style={{fontSize:10,color:'#1d4ed8',fontWeight:700}}>● Speaking</span>}
                                </div>
                                <div style={{display:'flex',gap:8}}>
                                    <button onClick={()=>{setVoiceOn(v=>{if(v)window.speechSynthesis?.cancel();return !v;});}}
                                        style={{background:voiceOn?'#eff6ff':'#f3f4f6',border:'1px solid '+(voiceOn?'#bfdbfe':'#e5e7eb'),borderRadius:6,padding:'4px 10px',cursor:'pointer',fontSize:12,color:voiceOn?'#1d4ed8':'#9ca3af'}}>
                                        {voiceOn ? '🔊 Voice On' : '🔇 Voice Off'}
                                    </button>
                                    <button onClick={()=>{window.speechSynthesis?.cancel();setTourMode(false);}} style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#9ca3af'}}>✕</button>
                                </div>
                            </div>
                            <div style={{fontSize:11,color:'#1d4ed8',fontWeight:700,textTransform:'uppercase',letterSpacing:1,marginBottom:6}}>Guided Tour</div>
                            <h2 style={{margin:'0 0 12px',fontSize:22,color:'#0d2137'}}>{s.t}</h2>
                            <p style={{color:'#4b5563',lineHeight:1.7,marginBottom:24,fontSize:14}}>{s.b}</p>
                            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                                {tourStep>0 && <button onClick={()=>go(tourStep-1)} style={{padding:'9px 20px',borderRadius:8,border:'1px solid #e5e7eb',background:'#fff',cursor:'pointer',fontWeight:600}}>Back</button>}
                                <button onClick={()=>{if(last){window.speechSynthesis?.cancel();setTourMode(false);}else{go(tourStep+1);}}}
                                    style={{padding:'9px 24px',borderRadius:8,border:'none',background:'#1d4ed8',color:'#fff',cursor:'pointer',fontWeight:700,fontSize:14}}>
                                    {last ? 'Finish' : 'Next'}
                                </button>
                            </div>
                            <div style={{display:'flex',gap:6,justifyContent:'center',marginTop:20}}>
                                {steps.map((_,i)=>(
                                    <div key={i} onClick={()=>go(i)} style={{width:i===tourStep?20:8,height:8,borderRadius:4,cursor:'pointer',transition:'all 0.2s',background:i===tourStep?'#1d4ed8':'#e5e7eb'}} />
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}